import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/auth/auth-context";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LoadingButton } from "@/components/shared/LoadingButton";
import {
  SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE,
  SmsConsentConfirmation,
} from "@/components/shared/SmsConsentConfirmation";
import { apiRequest } from "@/lib/api";
import { mapUiTypeToApi } from "@/lib/tickets";
import { fetchCompanySettings } from "@/lib/company";
import {
  invalidateClientData,
  invalidateProjectData,
  invalidateTicketData,
} from "@/lib/query-invalidation";
import { type PriceBookItem } from "@/lib/price-book";
import { ProjectContextSummary } from "@/components/projects/ProjectContextSummary";
import { GuidedTicketStart } from "@/components/tickets/GuidedTicketStart";
import {
  createClient,
  createProject,
  fetchClients,
  fetchProjects,
  type ClientItem,
  type ProjectItem,
  updateClient,
} from "@/lib/projects-clients";
import {
  getPriceBookUnitOptionsForType,
  resolvePriceBookUnitForType,
} from "@/lib/price-book-units";
import { fetchTemplates, type ApiTemplate } from "@/lib/templates";
import {
  deleteAttachment,
  uploadTicketAttachment,
  type UploadedAttachment,
} from "@/lib/attachments";
import {
  usePriceBookItems,
  useProjectTicketContextQuery,
} from "@/hooks/use-app-queries";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft,
  Camera,
  Check,
  ChevronDown,
  Eye,
  Loader2,
  Paperclip,
  Pen,
  Plus,
  Save,
  Send,
  Trash2,
} from "lucide-react";

type TicketType = "co" | "tm" | "ew";
type ApprovalMethod = "remote" | "onsite";
type ApprovalDeliveryMethod = "EMAIL" | "SMS" | "EMAIL_AND_SMS";
type PricingMode = "simple_total" | "internal_estimate";
type CustomerDisplayMode =
  | "final_price_only"
  | "summary_breakdown"
  | "full_itemized_breakdown";
type ChangeOrderReason =
  | "client_request"
  | "unforeseen_condition"
  | "material_change"
  | "scope_change"
  | "code_permit_requirement"
  | "other";
type ChangeOrderType = "add_cost" | "credit_deduct" | "no_cost_change";
type ExtraWorkReason =
  | "client_request"
  | "additional_scope"
  | "emergency_urgent_work"
  | "site_condition"
  | "after_hours_work"
  | "material_service_add_on"
  | "other";
type PricingCategory =
  | "labor"
  | "materials"
  | "equipment"
  | "subcontractor"
  | "other";
type PricingUnit =
  | "hrs"
  | "ea"
  | "box"
  | "bag"
  | "sheet"
  | "bucket"
  | "roll"
  | "ft"
  | "lf"
  | "sq_ft"
  | "gal"
  | "cu_yd"
  | "lb"
  | "day"
  | "fixed"
  | "other";
const COMMON_CADENCE_OPTIONS = [1, 2, 3, 5, 7, 10, 14, 21, 30];
const SMS_APPROVALS_ENABLED =
  ((import.meta.env.VITE_SMS_APPROVALS_ENABLED as string | undefined) ??
    "false") === "true";

type CreateTicketResponse = {
  id: string;
  ticketNumber: string;
};

type LineItemDirection = "ADD" | "DEDUCT";

type EditTicketResponse = {
  id: string;
  ticketNumber: string;
  type: "CHANGE_ORDER" | "TM_TICKET" | "EXTRA_WORK";
  project: { id: string; name: string };
  title: string;
  description?: string | null;
  showDescriptionToCustomer?: boolean;
  showAttachmentsToCustomer?: boolean;
  reasonImpact?: string | null;
  delayImpact?: boolean;
  delayDays?: number | null;
  internalNotes?: string | null;
  totalAmount?: string | number;
  subtotalAmount?: string | number;
  taxAmount: string | number;
  lineItems: Array<{
    id: string;
    direction?: LineItemDirection;
    category: string;
    description: string;
    details?: string | null;
    thumbnailPath?: string | null;
    thumbnailUrl?: string | null;
    quantity: string | number;
    unit: string;
    unitCost: string | number;
  }>;
  approvalRequests?: Array<{
    recipientName?: string | null;
    recipientEmail?: string | null;
    recipientPhone?: string | null;
    deliveryMethod?: ApprovalDeliveryMethod | null;
    ccEmails?: string[];
    message?: string | null;
  }>;
};

type LineItem = {
  id: number;
  category: string;
  direction: LineItemDirection;
  description: string;
  details: string;
  priceBookItemId?: string;
  thumbnailPath: string;
  thumbnailUrl?: string;
  quantity: string;
  unit: string;
  unitCost: string;
  pricingMethod: "HOURLY" | "FIXED";
  fixedAmount: string;
  useUnit: boolean;
};
type LineItemDefaults = Omit<LineItem, "id">;
type LineItemDefaultsByCategory = Record<LineItemCategory, LineItemDefaults>;

type SavedInternalEstimateRow = {
  id?: string;
  category?: string | null;
  description?: string | null;
  quantity?: string | number | null;
  unit?: string | null;
  unitCost?: string | number | null;
  lineTotal?: string | number | null;
  internalNote?: string | null;
  pricingMethod?: string | null;
  priceBookItemId?: string | null;
  thumbnailPath?: string | null;
  thumbnailUrl?: string | null;
};

type SavedPricingMetadata = {
  pricingVersion?: number;
  pricingMode?: PricingMode;
  customerDisplayMode?: CustomerDisplayMode;
  changeOrder?: {
    reasonForChange?: ChangeOrderReason;
    changeType?: ChangeOrderType;
  };
  extraWork?: {
    reason?: ExtraWorkReason;
  };
  internalEstimate?: {
    rows?: SavedInternalEstimateRow[];
  } | null;
};

type CapturedSignature = {
  signerName: string;
  signerTitle?: string;
  signerCompany?: string;
  capturedAt: string;
  imageDataUrl: string;
};

type SignaturePoint = { x: number; y: number };
type LineItemCategory = "LABOR" | "MATERIAL" | "EQUIPMENT" | "OTHER";
type AttachmentState = "ready" | "uploading" | "uploaded" | "failed";
type AttachmentItem = {
  id: number;
  name: string;
  size: number;
  mimeType: string;
  file: File;
  status: AttachmentState;
  progress: number;
  error?: string;
  previewUrl?: string;
  uploaded?: UploadedAttachment;
};

const CHANGE_ORDER_REASON_OPTIONS: Array<{
  value: ChangeOrderReason;
  label: string;
}> = [
  { value: "client_request", label: "Client request" },
  { value: "unforeseen_condition", label: "Unforeseen condition" },
  { value: "material_change", label: "Material change" },
  { value: "scope_change", label: "Scope change" },
  { value: "code_permit_requirement", label: "Code / permit requirement" },
  { value: "other", label: "Other" },
];

const CHANGE_ORDER_TYPE_OPTIONS: Array<{
  value: ChangeOrderType;
  label: string;
}> = [
  { value: "add_cost", label: "Add cost" },
  { value: "credit_deduct", label: "Credit / deduct" },
  { value: "no_cost_change", label: "No cost change" },
];

const EXTRA_WORK_REASON_OPTIONS: Array<{
  value: ExtraWorkReason;
  label: string;
}> = [
  { value: "client_request", label: "Client request" },
  { value: "additional_scope", label: "Additional scope" },
  { value: "emergency_urgent_work", label: "Emergency / urgent work" },
  { value: "site_condition", label: "Site condition" },
  { value: "after_hours_work", label: "After-hours work" },
  { value: "material_service_add_on", label: "Material/service add-on" },
  { value: "other", label: "Other" },
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value);

const normalizeLineItemCategory = (value: unknown): LineItemCategory => {
  const normalized = String(value ?? "")
    .trim()
    .toUpperCase();
  if (normalized === "LABOR" || normalized === "WORK") return "LABOR";
  if (normalized === "MATERIAL" || normalized === "MATERIALS")
    return "MATERIAL";
  if (normalized === "EQUIPMENT") return "EQUIPMENT";
  return "OTHER";
};

const defaultUnitByCategory = (category: LineItemCategory) => {
  return resolvePriceBookUnitForType(category);
};

const calculateLineItemTotal = (item: LineItem) => {
  const sign = item.direction === "DEDUCT" ? -1 : 1;
  if (item.pricingMethod === "FIXED") {
    return sign * Number(item.fixedAmount || 0);
  }
  return sign * Number(item.quantity || 0) * Number(item.unitCost || 0);
};

const normalizePhoneNumber = (value?: string | null) => {
  const raw = value?.trim();
  if (!raw) return "";
  if (raw.startsWith("+")) {
    const normalized = `+${raw.slice(1).replace(/\D/g, "")}`;
    return /^\+\d{10,15}$/.test(normalized) ? normalized : "";
  }
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return "";
};

const buildDefaultLineItemForCategory = (
  category: LineItemCategory,
  raw?: unknown,
): LineItemDefaults => {
  const item = (raw ?? {}) as Record<string, unknown>;
  const rawPricingMethod = String(item.pricingMethod ?? "").toUpperCase();
  const rawUnit = String(item.unit ?? "")
    .trim()
    .toLowerCase();
  const shouldUseFixedPricing =
    category === "OTHER" ||
    ((category === "LABOR" || category === "EQUIPMENT") &&
      rawPricingMethod === "FIXED") ||
    (category === "EQUIPMENT" &&
      !rawPricingMethod &&
      ["ea", "each", "unit"].includes(rawUnit) &&
      Number(item.quantity ?? 0) === 1 &&
      Number(item.unitCost ?? 0) > 0);
  const pricingMethod = shouldUseFixedPricing ? "FIXED" : "HOURLY";
  const resolvedUnit = resolvePriceBookUnitForType(
    category,
    String(item.unit ?? defaultUnitByCategory(category)),
  );

  return {
    category,
    direction:
      String(item.direction ?? "ADD").toUpperCase() === "DEDUCT"
        ? "DEDUCT"
        : "ADD",
    description: String(item.description ?? ""),
    details: String(item.details ?? ""),
    priceBookItemId: item.priceBookItemId ? String(item.priceBookItemId) : undefined,
    thumbnailPath: String(item.thumbnailPath ?? ""),
    thumbnailUrl: item.thumbnailUrl ? String(item.thumbnailUrl) : undefined,
    quantity: String(item.quantity ?? ""),
    unit: resolvedUnit,
    unitCost: String(item.unitCost ?? ""),
    pricingMethod,
    fixedAmount: String(
      shouldUseFixedPricing
        ? (item.fixedAmount ?? item.unitCost ?? "")
        : (item.fixedAmount ?? ""),
    ),
    useUnit: item.useUnit === false ? false : true,
  };
};

const defaultLineItemDefaultsByCategory = (
  raw?: unknown,
): LineItemDefaultsByCategory => {
  const source =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    LABOR: buildDefaultLineItemForCategory("LABOR", source.LABOR),
    MATERIAL: buildDefaultLineItemForCategory("MATERIAL", source.MATERIAL),
    EQUIPMENT: buildDefaultLineItemForCategory("EQUIPMENT", source.EQUIPMENT),
    OTHER: buildDefaultLineItemForCategory("OTHER", source.OTHER),
  };
};

const createLineItemFromDefaults = (
  id: number,
  category: LineItemCategory,
  defaultsMap: LineItemDefaultsByCategory,
): LineItem => ({
  id,
  ...buildDefaultLineItemForCategory(category, defaultsMap[category]),
});

const mapLineItemToPayload = (row: LineItem) => {
  const normalizedCategory = normalizeLineItemCategory(row.category);
  if (row.pricingMethod === "FIXED") {
    return {
      direction: row.direction,
      category: row.category,
      description: row.description,
      details: row.details.trim() || undefined,
      thumbnailPath: row.thumbnailPath.trim() || undefined,
      quantity: 1,
      unit: "fixed",
      unitCost: Number(row.fixedAmount || 0),
    };
  }

  return {
    direction: row.direction,
    category: row.category,
    description: row.description,
    details: row.details.trim() || undefined,
    thumbnailPath: row.thumbnailPath.trim() || undefined,
    quantity: Number(row.quantity || 0),
    unit:
      normalizedCategory === "MATERIAL" && !row.useUnit
        ? ""
        : resolvePriceBookUnitForType(
            normalizeLineItemCategory(row.category),
            row.unit,
          ),
    unitCost: Number(row.unitCost || 0),
  };
};

const normalizePricingCategory = (value?: string | null): PricingCategory => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s/-]+/g, "_");
  if (normalized.includes("labor") || normalized.includes("service"))
    return "labor";
  if (normalized.includes("material")) return "materials";
  if (normalized.includes("equipment")) return "equipment";
  if (normalized.includes("subcontract")) return "subcontractor";
  return "other";
};

const normalizePricingUnit = (value?: string | null): PricingUnit => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s/-]+/g, "_");
  if (["hr", "hrs", "hour", "hours"].includes(normalized)) return "hrs";
  if (["ea", "each", "unit", "units"].includes(normalized)) return "ea";
  if (["box", "boxes"].includes(normalized)) return "box";
  if (["bag", "bags"].includes(normalized)) return "bag";
  if (["sheet", "sheets"].includes(normalized)) return "sheet";
  if (["bucket", "buckets"].includes(normalized)) return "bucket";
  if (["roll", "rolls"].includes(normalized)) return "roll";
  if (["ft", "foot", "feet"].includes(normalized)) return "ft";
  if (["lf", "linear_ft", "linear_foot", "linear_feet"].includes(normalized)) return "lf";
  if (["sq_ft", "sqft", "sf"].includes(normalized)) return "sq_ft";
  if (["gal", "gallon", "gallons"].includes(normalized)) return "gal";
  if (["cu_yd", "cuyd", "cubic_yard", "cubic_yards"].includes(normalized)) return "cu_yd";
  if (["lb", "lbs", "pound", "pounds"].includes(normalized)) return "lb";
  if (["day", "days"].includes(normalized)) return "day";
  if (["fixed", "flat", "no_units"].includes(normalized)) return "fixed";
  return "other";
};

const isCustomerDisplayMode = (
  value: unknown,
): value is CustomerDisplayMode =>
  value === "final_price_only" ||
  value === "summary_breakdown" ||
  value === "full_itemized_breakdown";

const parseTicketPricingMetadata = (
  value?: string | null,
): SavedPricingMetadata | null => {
  if (!value?.trim()) return null;
  try {
    const parsed = JSON.parse(value) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    return parsed as SavedPricingMetadata;
  } catch {
    return null;
  }
};

const buildLineItemsFromSavedInternalEstimateRows = (
  rows: SavedInternalEstimateRow[] | undefined,
) => {
  const baseId = Date.now();
  return (rows ?? [])
    .filter((row) => String(row.description ?? "").trim().length > 0)
    .map<LineItem>((row, idx) => {
      const category = normalizeLineItemCategory(row.category);
      const method = String(row.pricingMethod ?? "")
        .trim()
        .toLowerCase();
      const lineTotal = Number(row.lineTotal ?? 0);
      const unitCost = Number(row.unitCost ?? 0);
      const isFlatCost =
        method === "flat_cost" ||
        method === "fixed" ||
        method === "no_units" ||
        normalizePricingUnit(row.unit) === "fixed";
      const fixedAmount = Math.abs(
        Number.isFinite(lineTotal) && lineTotal !== 0 ? lineTotal : unitCost,
      );

      return {
        id: baseId + idx,
        category,
        direction: lineTotal < 0 ? "DEDUCT" : "ADD",
        description: String(row.description ?? ""),
        details: String(row.internalNote ?? ""),
        priceBookItemId: row.priceBookItemId ? String(row.priceBookItemId) : undefined,
        thumbnailPath: String(row.thumbnailPath ?? ""),
        thumbnailUrl: row.thumbnailUrl ? String(row.thumbnailUrl) : undefined,
        quantity: isFlatCost ? "1" : String(row.quantity ?? ""),
        unit: isFlatCost
          ? "fixed"
          : resolvePriceBookUnitForType(category, row.unit),
        unitCost: String(row.unitCost ?? ""),
        pricingMethod: isFlatCost ? "FIXED" : "HOURLY",
        fixedAmount: isFlatCost ? String(fixedAmount || "") : "",
        useUnit: !isFlatCost,
      };
    });
};

const lineItemHasMeaningfulContentWithoutDescription = (row: LineItem) => {
  if (row.description.trim().length > 0) return false;
  return (
    row.details.trim().length > 0 ||
    row.thumbnailPath.trim().length > 0 ||
    Boolean(row.thumbnailUrl?.trim()) ||
    Number(row.quantity || 0) > 0 ||
    Number(row.unitCost || 0) > 0 ||
    Number(row.fixedAmount || 0) > 0
  );
};

const materialUnitOptions = getPriceBookUnitOptionsForType("MATERIAL");
const ALLOWED_IMAGE_MIME = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
]);
const IMAGE_EXT_RE = /\.(jpe?g|png|webp|heic|heif)$/i;
const PDF_EXT_RE = /\.pdf$/i;
const MAX_IMAGE_BYTES = 1 * 1024 * 1024;
const MAX_PDF_BYTES = 100 * 1024;

const mapApiTypeToUi = (type: EditTicketResponse["type"]): TicketType => {
  if (type === "CHANGE_ORDER") return "co";
  if (type === "TM_TICKET") return "tm";
  return "ew";
};

const normalizeTemplateSubtype = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, "");

const TICKET_TEMPLATE_SUBTYPE_ALIASES: Record<TicketType, string[]> = {
  tm: ["tmticket", "tm", "estimate", "tandmticket"],
  co: ["changeorder", "co"],
  ew: ["extrawork", "ew"],
};

const templateMatchesTicketType = (template: ApiTemplate, type: TicketType) => {
  const subtype = normalizeTemplateSubtype(template.subType ?? "");
  if (TICKET_TEMPLATE_SUBTYPE_ALIASES[type].includes(subtype)) return true;

  const name = (template.name ?? "").toLowerCase();
  if (type === "co") return name.includes("change order");
  if (type === "ew") return name.includes("extra work");
  return name.includes("t&m") || name.includes("estimate");
};

const normalizeTicketTypeQuery = (value: string | null): TicketType | null => {
  if (value === "tm" || value === "estimate") return "tm";
  if (value === "co" || value === "change_order") return "co";
  if (value === "ew" || value === "extra_work") return "ew";
  return null;
};

export default function CreateTicket() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const lineItemOptions: Array<{ value: LineItemCategory; label: string }> =
    useMemo(
      () => [
        { value: "LABOR", label: t("ticketDetail.categories.labor") },
        { value: "MATERIAL", label: t("ticketDetail.categories.materials") },
        { value: "EQUIPMENT", label: t("ticketDetail.categories.equipment") },
        { value: "OTHER", label: t("ticketDetail.categories.other") },
      ],
      [t],
    );

  const descriptionPlaceholderForCategory = (category: LineItemCategory) => {
    if (category === "LABOR") return t("createTicket.placeholders.labor");
    if (category === "MATERIAL") return t("createTicket.placeholders.material");
    if (category === "EQUIPMENT")
      return t("createTicket.placeholders.equipment");
    return t("createTicket.placeholders.other");
  };

  const lineDescriptionShouldExpand = useCallback((value: string) => {
    return value.trim().length > 70 || value.includes("\n");
  }, []);

  const syncLineDescriptionHeight = useCallback(
    (element: HTMLTextAreaElement, value: string) => {
      const collapsedHeight = 40;
      const expandedMaxHeight = 128;

      if (!lineDescriptionShouldExpand(value)) {
        element.style.height = `${collapsedHeight}px`;
        element.style.overflowY = "hidden";
        return;
      }

      element.style.height = `${collapsedHeight}px`;
      const nextHeight = Math.min(element.scrollHeight, expandedMaxHeight);
      element.style.height = `${nextHeight}px`;
      element.style.overflowY =
        element.scrollHeight > expandedMaxHeight ? "auto" : "hidden";
    },
    [lineDescriptionShouldExpand],
  );

  const lineDescriptionClassName = useCallback(
    (value: string) =>
      lineDescriptionShouldExpand(value)
        ? "min-h-[72px] max-h-32 resize-none overflow-y-auto py-2 leading-5 text-base md:text-sm"
        : "h-10 min-h-0 max-h-10 resize-none overflow-hidden py-2 leading-5 text-base md:text-sm",
    [lineDescriptionShouldExpand],
  );

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const editTicketId = searchParams.get("edit");
  const isEditMode = Boolean(editTicketId);

  const [ticketType, setTicketType] = useState<TicketType>("tm");
  const [ticketTypeOrder, setTicketTypeOrder] = useState<Array<TicketType>>([
    "tm",
    "ew",
    "co",
  ]);
  const [projectId, setProjectId] = useState("");
  const [title, setTitle] = useState("");
  const [titleDirty, setTitleDirty] = useState(false);
  const [description, setDescription] = useState("");
  const [showDescriptionToCustomer, setShowDescriptionToCustomer] =
    useState(true);
  const [changeOrderReason, setChangeOrderReason] =
    useState<ChangeOrderReason>("client_request");
  const [changeOrderType, setChangeOrderType] =
    useState<ChangeOrderType>("add_cost");
  const [extraWorkReason, setExtraWorkReason] =
    useState<ExtraWorkReason>("client_request");
  const [showAttachmentsToCustomer, setShowAttachmentsToCustomer] =
    useState(false);
  const [defaultTaxRate, setDefaultTaxRate] = useState(0);
  const [taxAmount, setTaxAmount] = useState("0");
  const [taxManuallyOverridden, setTaxManuallyOverridden] = useState(false);

  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [clients, setClients] = useState<ClientItem[]>([]);
  const [ticketTemplates, setTicketTemplates] = useState<ApiTemplate[]>([]);
  const [ticketDefaultTitlesByType, setTicketDefaultTitlesByType] = useState<
    Record<TicketType, string>
  >({
    tm: t("createTicket.defaultTitles.estimate"),
    co: t("createTicket.defaultTitles.changeOrderEstimate"),
    ew: t("createTicket.defaultTitles.extraWorkEstimate"),
  });
  const [lookupLoading, setLookupLoading] = useState(true);
  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const [projectForm, setProjectForm] = useState({
    name: "",
    code: "",
    clientId: "",
    jobSiteAddress: "",
  });
  const [projectSaving, setProjectSaving] = useState(false);
  const [addProjectClientOpen, setAddProjectClientOpen] = useState(false);
  const [newProjectClientName, setNewProjectClientName] = useState("");
  const [newProjectClientEmail, setNewProjectClientEmail] = useState("");
  const [creatingProjectClient, setCreatingProjectClient] = useState(false);

  const [approvalMethod, setApprovalMethod] =
    useState<ApprovalMethod>("remote");
  const [deliveryMethod, setDeliveryMethod] =
    useState<ApprovalDeliveryMethod>("EMAIL");
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [recipientNameTouched, setRecipientNameTouched] = useState(false);
  const [recipientEmailTouched, setRecipientEmailTouched] = useState(false);
  const [recipientPhoneTouched, setRecipientPhoneTouched] = useState(false);
  const [recipientNameError, setRecipientNameError] = useState("");
  const [recipientEmailError, setRecipientEmailError] = useState("");
  const [recipientPhoneError, setRecipientPhoneError] = useState("");
  const [smsConsentConfirmed, setSmsConsentConfirmed] = useState(false);
  const [smsConsentError, setSmsConsentError] = useState("");
  const [saveRecipientAsClientPrimary, setSaveRecipientAsClientPrimary] =
    useState(false);
  const [message, setMessage] = useState("");
  const [workspaceAutoRemindersEnabled, setWorkspaceAutoRemindersEnabled] =
    useState(true);
  const [reminderCadenceOption, setReminderCadenceOption] = useState("3");
  const [customReminderCadenceDays, setCustomReminderCadenceDays] =
    useState("");
  const [lineItemDefaultsByCategory, setLineItemDefaultsByCategory] =
    useState<LineItemDefaultsByCategory>(() =>
      defaultLineItemDefaultsByCategory(),
    );
  const [activePriceBookLineId, setActivePriceBookLineId] = useState<
    number | null
  >(null);
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [createdTicketForUploads, setCreatedTicketForUploads] =
    useState<CreateTicketResponse | null>(null);
  const [processingUploads, setProcessingUploads] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const attachmentsRef = useRef<AttachmentItem[]>([]);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [capturingPhoto, setCapturingPhoto] = useState(false);
  const cameraVideoRef = useRef<HTMLVideoElement | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureFormName, setSignatureFormName] = useState("");
  const [signatureFormTitle, setSignatureFormTitle] = useState("");
  const [signatureFormCompany, setSignatureFormCompany] = useState("");
  const [capturedSignature, setCapturedSignature] =
    useState<CapturedSignature | null>(null);
  const [onsitePreviewOpen, setOnsitePreviewOpen] = useState(false);
  const [onsitePreviewUrl, setOnsitePreviewUrl] = useState<string | null>(null);
  const [preparingOnsitePreview, setPreparingOnsitePreview] = useState(false);
  const [finalizingOnsiteApproval, setFinalizingOnsiteApproval] =
    useState(false);
  const [signatureRequestedFromPreview, setSignatureRequestedFromPreview] =
    useState(false);
  const [signatureStrokes, setSignatureStrokes] = useState<SignaturePoint[][]>(
    [],
  );
  const [signatureError, setSignatureError] = useState("");
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const activeStrokeRef = useRef<SignaturePoint[]>([]);
  const lastPointRef = useRef<SignaturePoint | null>(null);
  const hydratedDraftRef = useRef(false);
  const autosaveTimeoutRef = useRef<number | null>(null);
  const lastAutosavedSnapshotRef = useRef("");
  const autosaveInFlightRef = useRef(false);
  const skipAutosaveOnUnmountRef = useRef(false);
  const persistedTicketIdRef = useRef<string | null>(editTicketId ?? null);
  const inFlightCreateTicketRef = useRef<Promise<CreateTicketResponse> | null>(
    null,
  );
  const skipNextTemplateAutofillRef = useRef(false);
  const skipNextProjectRecipientResetRef = useRef(false);

  const [lineItems, setLineItems] = useState<LineItem[]>([
    createLineItemFromDefaults(1, "LABOR", defaultLineItemDefaultsByCategory()),
  ]);
  const [pricingMode, setPricingMode] = useState<PricingMode>("simple_total");
  const [customerPrice, setCustomerPrice] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const [customerDisplayMode, setCustomerDisplayMode] =
    useState<CustomerDisplayMode>("final_price_only");
  const [estimateCalculatorOpen, setEstimateCalculatorOpen] = useState(false);
  const [estimatePreviewOpen, setEstimatePreviewOpen] = useState(false);
  const [internalEstimateSaved, setInternalEstimateSaved] = useState(false);
  const [customerPriceManuallyOverridden, setCustomerPriceManuallyOverridden] =
    useState(false);

  const [savingDraft, setSavingDraft] = useState(false);
  const [savingSend, setSavingSend] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [zeroTotalConfirmOpen, setZeroTotalConfirmOpen] = useState(false);
  const isSubmitting = savingDraft || savingSend || processingUploads;
  const starterAttachmentLimitReached =
    user?.currentPlan === "STARTER" && attachments.length >= 1;
  const priceBookQuery = usePriceBookItems();
  const nextProjectCode = (offset = 1) =>
    `PRJ-${String(projects.length + offset).padStart(4, "0")}`;
  const clearCurrentDraft = useCallback(() => {
    skipAutosaveOnUnmountRef.current = true;
    if (autosaveTimeoutRef.current) {
      window.clearTimeout(autosaveTimeoutRef.current);
      autosaveTimeoutRef.current = null;
    }
    lastAutosavedSnapshotRef.current = "";
  }, []);
  const defaultTitleForType = useMemo(
    () =>
      ticketDefaultTitlesByType[ticketType] ??
      t("createTicket.defaultTitles.estimate"),
    [ticketDefaultTitlesByType, ticketType, t],
  );
  const titlePlaceholder = useMemo(() => {
    if (ticketType === "tm") return "e.g. Water damage repair estimate";
    if (ticketType === "co") return "e.g. Additional drywall repair";
    return "e.g. After-hours leak repair";
  }, [ticketType]);
  const descriptionInputPlaceholder =
    "Describe the work for the customer. Turn off Show to customer to keep it internal.";

  useEffect(() => {
    if (isEditMode) return;
    if (ticketType !== "ew") return;
    if (titleDirty) return;
    if (title.trim()) return;
    setTitle(defaultTitleForType);
  }, [defaultTitleForType, isEditMode, ticketType, title, titleDirty]);

  const allDefaultTitles = useMemo(
    () => Object.values(ticketDefaultTitlesByType),
    [ticketDefaultTitlesByType],
  );
  const descriptionLabel = useMemo(() => {
    if (ticketType === "co") return t("createTicket.changeOrderDetail");
    if (ticketType === "ew") return t("createTicket.extraWorkDetail");
    return t("ticketDetail.description");
  }, [ticketType, t]);
  const effectiveDescriptionPlaceholder =
    ticketType === "co"
      ? "Describe what changed, why it changed, and what the customer is approving."
      : ticketType === "ew"
        ? "Describe the extra work, why it is needed, and what is included."
      : descriptionInputPlaceholder;

  useEffect(() => {
    const requestedType = normalizeTicketTypeQuery(searchParams.get("type"));
    if (requestedType) {
      setTicketType(requestedType);
    }
  }, [searchParams]);

  useEffect(() => {
    hydratedDraftRef.current = true;
  }, [isEditMode, searchParams]);

  useEffect(() => {
    persistedTicketIdRef.current = editTicketId ?? null;
    inFlightCreateTicketRef.current = null;
  }, [editTicketId]);

  useEffect(() => {
    const loadLookups = async () => {
      setLookupLoading(true);
      try {
        const [projectsData, clientsData, settings, templateRes] =
          await Promise.all([
            fetchProjects(),
            fetchClients(),
            fetchCompanySettings(),
            fetchTemplates({ category: "ticket" }),
          ]);
        setProjects(projectsData.filter((p) => p.isActive));
        setClients(clientsData);
        setTicketTemplates(templateRes.items);
        const nextDefaultTitles: Record<TicketType, string> = {
          tm: t("createTicket.defaultTitles.estimate"),
          co: t("createTicket.defaultTitles.changeOrderEstimate"),
          ew: t("createTicket.defaultTitles.extraWorkEstimate"),
        };
        (["tm", "co", "ew"] as TicketType[]).forEach((typeKey) => {
          const candidates = templateRes.items.filter((template) =>
            templateMatchesTicketType(template, typeKey),
          );
          const preferred =
            candidates.find((template) => template.isDefault) ?? candidates[0];
          if (!preferred) return;
          const data = (preferred.dataJson ?? {}) as Record<string, unknown>;
          const defaultTitleValue = String(data.defaultTitle ?? "").trim();
          if (defaultTitleValue) nextDefaultTitles[typeKey] = defaultTitleValue;
        });
        setTicketDefaultTitlesByType(nextDefaultTitles);
        const nextTicketTypeOrder: TicketType[] =
          settings.ticketTypeOrder?.length === 3
            ? settings.ticketTypeOrder
            : (["tm", "ew", "co"] as TicketType[]);
        setTicketTypeOrder(nextTicketTypeOrder);
        if (!isEditMode) {
          const requestedType = normalizeTicketTypeQuery(
            searchParams.get("type"),
          );
          if (!requestedType) {
            setTicketType(nextTicketTypeOrder[0]);
          }
        }
        const autoRemindersEnabled = settings.reminderDayOffsets.length > 0;
        setWorkspaceAutoRemindersEnabled(autoRemindersEnabled);
        const defaultCadence = settings.reminderDayOffsets?.[0];
        setDefaultTaxRate(Number(settings.defaultTaxRate ?? 0));
        if (
          autoRemindersEnabled &&
          defaultCadence &&
          Number.isInteger(defaultCadence) &&
          defaultCadence > 0
        ) {
          if (COMMON_CADENCE_OPTIONS.includes(defaultCadence)) {
            setReminderCadenceOption(String(defaultCadence));
          } else {
            setReminderCadenceOption("custom");
            setCustomReminderCadenceDays(String(defaultCadence));
          }
        }
      } catch (e) {
        toast({
          title: t("createTicket.toasts.lookupLoadFailedTitle"),
          description:
            e instanceof Error
              ? e.message
              : t("createTicket.toasts.lookupLoadFailedDescription"),
          variant: "destructive",
        });
      } finally {
        setLookupLoading(false);
      }
    };

    void loadLookups();
  }, [isEditMode, searchParams]);

  const orderedTicketTypeTabs = useMemo(
    () =>
      ticketTypeOrder.map((key) => ({
        key,
        label:
          key === "tm"
            ? t("createTicket.tabs.estimate")
            : key === "co"
              ? t("createTicket.tabs.changeOrder")
              : t("createTicket.tabs.extraWork"),
      })),
    [ticketTypeOrder, t],
  );
  const selectedTicketTypeLabel = useMemo(
    () =>
      orderedTicketTypeTabs.find((item) => item.key === ticketType)?.label ??
      "",
    [orderedTicketTypeTabs, ticketType],
  );

  const applyTicketTemplate = useCallback(
    (template: ApiTemplate, options?: { forceTitle?: boolean }) => {
      const data = template.dataJson ?? {};
      const linePreset: Record<string, unknown>[] = Array.isArray(
        data.defaultLineItems,
      )
        ? data.defaultLineItems.filter(
            (row): row is Record<string, unknown> =>
              Boolean(row) && typeof row === "object",
          )
        : [];
      const nextLineItemDefaults = defaultLineItemDefaultsByCategory(
        (data as Record<string, unknown>).lineItemDefaultsByCategory,
      );

      setLineItemDefaultsByCategory(nextLineItemDefaults);

      if (
        typeof data.defaultReminderCadenceDays === "number" &&
        data.defaultReminderCadenceDays > 0
      ) {
        const cadence = Number(data.defaultReminderCadenceDays);
        if (COMMON_CADENCE_OPTIONS.includes(cadence)) {
          setReminderCadenceOption(String(cadence));
          setCustomReminderCadenceDays("");
        } else {
          setReminderCadenceOption("custom");
          setCustomReminderCadenceDays(String(cadence));
        }
      }

      if (linePreset.length > 0) {
        setLineItems(
          linePreset.map((row, idx) => {
            const item = row as Record<string, unknown>;
            const normalizedCategory = normalizeLineItemCategory(item.category);
            return {
              id: Date.now() + idx,
              category: normalizedCategory,
              direction:
                String(item.direction ?? "ADD").toUpperCase() === "DEDUCT"
                  ? "DEDUCT"
                  : "ADD",
              description: String(item.description ?? ""),
              details: String(item.details ?? ""),
              thumbnailPath: String(item.thumbnailPath ?? ""),
              thumbnailUrl: item.thumbnailUrl
                ? String(item.thumbnailUrl)
                : undefined,
              quantity: String(item.quantity ?? ""),
              unit:
                normalizedCategory === "EQUIPMENT"
                  ? String(
                      item.unit ?? defaultUnitByCategory(normalizedCategory),
                    ) || "hrs"
                  : String(
                      item.unit ?? defaultUnitByCategory(normalizedCategory),
                    ),
              unitCost: String(item.unitCost ?? ""),
              pricingMethod:
                normalizedCategory === "LABOR" ||
                normalizedCategory === "EQUIPMENT"
                  ? String(item.pricingMethod ?? "").toUpperCase() === "FIXED"
                    ? "FIXED"
                    : "HOURLY"
                  : "HOURLY",
              fixedAmount: String(item.fixedAmount ?? item.unitCost ?? ""),
              useUnit: item.useUnit === false ? false : true,
            };
          }),
        );
      }
    },
    [],
  );

  useEffect(() => {
    if (!ticketTemplates.length) return;
    if (isEditMode) return;
    if (skipNextTemplateAutofillRef.current) {
      skipNextTemplateAutofillRef.current = false;
      return;
    }
    const candidates = ticketTemplates.filter((template) =>
      templateMatchesTicketType(template, ticketType),
    );
    const preferred = candidates.find((t) => t.isDefault) ?? candidates[0];
    if (!preferred) return;
    applyTicketTemplate(preferred, { forceTitle: true });
  }, [ticketType, ticketTemplates, applyTicketTemplate, isEditMode]);

  useEffect(() => {
    if (lookupLoading || projectId) return;
    if (isEditMode) return;
    const preferredProjectId = searchParams.get("projectId");
    if (preferredProjectId) {
      const foundProject = projects.find(
        (project) => project.id === preferredProjectId,
      );
      if (foundProject) {
        setProjectId(foundProject.id);
      } else {
        toast({
          title: t("createTicket.toasts.projectNotFoundTitle"),
          description: t("createTicket.toasts.projectNotFoundDescription"),
          variant: "destructive",
        });
      }
      const next = new URLSearchParams(searchParams);
      next.delete("projectId");
      next.delete("clientId");
      setSearchParams(next, { replace: true });
      return;
    }
    const preferredClientId = searchParams.get("clientId");
    if (!preferredClientId) return;
    const preferredProject = projects.find(
      (project) => project.clientId === preferredClientId,
    );
    if (preferredProject) {
      setProjectId(preferredProject.id);
    } else {
      toast({
        title: t("createTicket.toasts.noProjectLinkedTitle"),
        description: t("createTicket.toasts.noProjectLinkedDescription"),
      });
    }
    const next = new URLSearchParams(searchParams);
    next.delete("clientId");
    setSearchParams(next, { replace: true });
  }, [
    lookupLoading,
    projectId,
    projects,
    searchParams,
    setSearchParams,
    isEditMode,
  ]);

  useEffect(() => {
    const loadForEdit = async () => {
      if (!editTicketId) return;
      setLoadingEdit(true);
      try {
        const ticket = await apiRequest<EditTicketResponse>(
          `/tickets/${editTicketId}`,
          {
            method: "GET",
          },
        );
        setCreatedTicketForUploads({
          id: ticket.id,
          ticketNumber: ticket.ticketNumber,
        });
        const mappedType = mapApiTypeToUi(ticket.type);
        setTicketType(mappedType);
        setProjectId(ticket.project.id);
        setTitleDirty(true);
        setTitle(ticket.title ?? "");
        const fallbackDetail =
          mappedType === "co"
            ? (ticket.reasonImpact ?? "")
            : mappedType === "ew"
              ? (ticket.reasonImpact ?? "")
              : "";
        setDescription(
          ticket.description?.trim() ? ticket.description : fallbackDetail,
        );
        setShowDescriptionToCustomer(
          mappedType === "tm"
            ? (ticket.showDescriptionToCustomer ?? true)
            : true,
        );
        const pricingMetadata = parseTicketPricingMetadata(
          ticket.internalNotes,
        );
        if (mappedType === "co") {
          const nextReason = pricingMetadata?.changeOrder?.reasonForChange;
          const nextType = pricingMetadata?.changeOrder?.changeType;
          if (
            nextReason &&
            CHANGE_ORDER_REASON_OPTIONS.some(
              (option) => option.value === nextReason,
            )
          ) {
            setChangeOrderReason(nextReason);
          }
          if (
            nextType &&
            CHANGE_ORDER_TYPE_OPTIONS.some(
              (option) => option.value === nextType,
            )
          ) {
            setChangeOrderType(nextType);
          }
        }
        if (mappedType === "ew") {
          const nextReason = pricingMetadata?.extraWork?.reason;
          if (
            nextReason &&
            EXTRA_WORK_REASON_OPTIONS.some(
              (option) => option.value === nextReason,
            )
          ) {
            setExtraWorkReason(nextReason);
          }
        }
        setShowAttachmentsToCustomer(ticket.showAttachmentsToCustomer ?? false);
        setTaxAmount(String(ticket.taxAmount ?? "0"));
        setTaxManuallyOverridden(true);
        setCustomerPrice(
          String(ticket.subtotalAmount ?? ticket.totalAmount ?? ""),
        );
        setCustomerPriceManuallyOverridden(
          Boolean(ticket.subtotalAmount ?? ticket.totalAmount),
        );
        const savedInternalEstimateRows =
          buildLineItemsFromSavedInternalEstimateRows(
            pricingMetadata?.internalEstimate?.rows,
          );
        const hasSavedInternalEstimateRows =
          savedInternalEstimateRows.length > 0;
        setPricingMode(
          hasSavedInternalEstimateRows ? "internal_estimate" : "simple_total",
        );
        setInternalEstimateSaved(hasSavedInternalEstimateRows);
        if (isCustomerDisplayMode(pricingMetadata?.customerDisplayMode)) {
          setCustomerDisplayMode(pricingMetadata.customerDisplayMode);
        }
        setLineItems(
          hasSavedInternalEstimateRows
            ? savedInternalEstimateRows
            : ticket.lineItems.length > 0
              ? ticket.lineItems.map((row, idx) => ({
                  id: Date.now() + idx,
                  category: normalizeLineItemCategory(row.category),
                  direction: row.direction === "DEDUCT" ? "DEDUCT" : "ADD",
                  description: row.description ?? "",
                  details: row.details ?? "",
                  thumbnailPath: row.thumbnailPath ?? "",
                  thumbnailUrl: row.thumbnailUrl ?? undefined,
                  quantity: String(row.quantity ?? ""),
                  unit:
                    normalizeLineItemCategory(row.category) === "EQUIPMENT"
                      ? row.unit || "hrs"
                      : (row.unit ?? "ea"),
                  unitCost: String(row.unitCost ?? ""),
                  pricingMethod:
                    normalizeLineItemCategory(row.category) === "LABOR" ||
                    normalizeLineItemCategory(row.category) === "EQUIPMENT"
                      ? String(
                          (row as unknown as Record<string, unknown>)
                            .pricingMethod ?? "",
                        ).toUpperCase() === "FIXED"
                        ? "FIXED"
                        : "HOURLY"
                      : "HOURLY",
                  fixedAmount: String(row.unitCost ?? ""),
                  useUnit: row.unit !== "",
                }))
              : [
                  {
                    id: 1,
                    category: "LABOR",
                    direction: "ADD",
                    description: "",
                    details: "",
                    thumbnailPath: "",
                    quantity: "",
                    unit: "hrs",
                    unitCost: "",
                    pricingMethod: "HOURLY",
                    fixedAmount: "",
                    useUnit: true,
                  },
                ],
        );

        const latestApproval = ticket.approvalRequests?.[0];
        if (latestApproval?.recipientName)
          setRecipientName(latestApproval.recipientName);
        if (latestApproval?.recipientEmail)
          setRecipientEmail(latestApproval.recipientEmail);
        if (latestApproval?.recipientPhone)
          setRecipientPhone(latestApproval.recipientPhone);
        if (latestApproval?.deliveryMethod)
          setDeliveryMethod(latestApproval.deliveryMethod);
        if (latestApproval?.message) setMessage(latestApproval.message);
      } catch (e) {
        toast({
          title: t("createTicket.toasts.loadTicketFailedTitle"),
          description:
            e instanceof Error
              ? e.message
              : t("createTicket.toasts.loadTicketFailedDescription"),
          variant: "destructive",
        });
      } finally {
        setLoadingEdit(false);
      }
    };
    void loadForEdit();
  }, [editTicketId]);

  useEffect(() => {
    if (lookupLoading || !projectId) return;
    if (projects.some((project) => project.id === projectId)) return;
    setProjectId("");
  }, [lookupLoading, projectId, projects]);

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === projectId) ?? null,
    [projectId, projects],
  );
  const selectedProjectContextQuery = useProjectTicketContextQuery(projectId);
  const projectContextSummaryLine = useMemo(() => {
    const count = selectedProjectContextQuery.data?.tickets.length ?? 0;
    const ticketLabel = count === 1 ? "1 ticket" : `${count} tickets`;
    if (!selectedProjectContextQuery.data?.lastTicketCreatedAt)
      return ticketLabel;
    return `${ticketLabel} - Last ticket ${new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(selectedProjectContextQuery.data.lastTicketCreatedAt))}`;
  }, [selectedProjectContextQuery.data]);
  const changeOrderContractSummary = useMemo(() => {
    const context = selectedProjectContextQuery.data;
    if (!context) {
      return {
        approvedEstimateTotal: 0,
        approvedChangeOrderAdditionsTotal: 0,
        approvedChangeOrderDeductionsTotal: 0,
        currentContractTotal: 0,
      };
    }

    return {
      approvedEstimateTotal: context.approvedEstimateTotal ?? 0,
      approvedChangeOrderAdditionsTotal:
        context.approvedChangeOrderAdditionsTotal ?? 0,
      approvedChangeOrderDeductionsTotal:
        context.approvedChangeOrderDeductionsTotal ?? 0,
      currentContractTotal:
        context.approvedTotal ?? context.currentContractTotal ?? 0,
    };
  }, [selectedProjectContextQuery.data]);
  const changeOrderAmount = useMemo(() => {
    if (changeOrderType === "no_cost_change") return 0;
    const amount = Number(customerPrice || 0);
    if (!Number.isFinite(amount) || amount <= 0) return 0;
    return changeOrderType === "credit_deduct" ? -amount : amount;
  }, [changeOrderType, customerPrice]);
  const projectedContractTotal =
    changeOrderContractSummary.currentContractTotal + changeOrderAmount;
  const changeOrderReasonLabel =
    CHANGE_ORDER_REASON_OPTIONS.find(
      (option) => option.value === changeOrderReason,
    )?.label ?? "Client request";
  const changeOrderTypeLabel =
    CHANGE_ORDER_TYPE_OPTIONS.find((option) => option.value === changeOrderType)
      ?.label ?? "Add cost";
  const extraWorkReasonLabel =
    EXTRA_WORK_REASON_OPTIONS.find((option) => option.value === extraWorkReason)
      ?.label ?? "Client request";
  const selectedClientPrimaryEmail =
    selectedProject?.client?.email?.trim() || "";
  const selectedClientPrimaryPhone =
    selectedProject?.client?.phone?.trim() || "";
  const normalizedSelectedClientPhone = useMemo(
    () => normalizePhoneNumber(selectedClientPrimaryPhone),
    [selectedClientPrimaryPhone],
  );
  const smsAllowedByPlan = user?.currentPlan !== "STARTER";
  const canUseSmsDelivery =
    SMS_APPROVALS_ENABLED &&
    smsAllowedByPlan &&
    Boolean(
      normalizedSelectedClientPhone || normalizePhoneNumber(recipientPhone),
    );
  const smsAvailabilityReason = !SMS_APPROVALS_ENABLED
    ? t("createTicket.smsUnavailableFlag")
    : !smsAllowedByPlan
      ? t("createTicket.smsUnavailablePlan")
      : !canUseSmsDelivery
        ? t("createTicket.smsUnavailableNoPhone")
        : "";
  const deliveryUsesSms =
    deliveryMethod === "SMS" || deliveryMethod === "EMAIL_AND_SMS";
  const hasClientPrimaryEmail = Boolean(selectedClientPrimaryEmail);
  const recipientEmailEntries = useMemo(
    () =>
      recipientEmail
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean),
    [recipientEmail],
  );
  const primaryRecipientEmail =
    recipientEmailEntries[0] || selectedClientPrimaryEmail;
  const ccRecipientEmails = recipientEmailEntries.slice(1);
  const effectiveRecipientEmail = primaryRecipientEmail;
  const signatureAutofillName =
    recipientName.trim() || selectedProject?.client?.name?.trim() || "";
  const signatureAutofillTitle = selectedProject?.client?.type?.trim() || "";
  const signatureAutofillCompany =
    selectedProject?.client?.companyName?.trim() || "";

  useEffect(() => {
    if (
      !recipientEmailTouched &&
      !recipientEmail.trim() &&
      selectedClientPrimaryEmail
    ) {
      setRecipientEmail(selectedClientPrimaryEmail);
    }
    if (
      !recipientNameTouched &&
      !recipientName.trim() &&
      selectedProject?.client?.name
    ) {
      setRecipientName(selectedProject.client.name);
    }
    if (
      !recipientPhoneTouched &&
      !recipientPhone.trim() &&
      normalizedSelectedClientPhone
    ) {
      setRecipientPhone(normalizedSelectedClientPhone);
    }
  }, [
    recipientEmail,
    recipientPhone,
    recipientName,
    recipientEmailTouched,
    recipientPhoneTouched,
    recipientNameTouched,
    selectedClientPrimaryEmail,
    normalizedSelectedClientPhone,
    selectedProject?.client?.name,
  ]);

  useEffect(() => {
    if (skipNextProjectRecipientResetRef.current) {
      skipNextProjectRecipientResetRef.current = false;
      return;
    }
    setRecipientNameTouched(false);
    setRecipientEmailTouched(false);
    setRecipientPhoneTouched(false);
    setRecipientName(selectedProject?.client?.name ?? "");
    setRecipientEmail(selectedProject?.client?.email?.trim() ?? "");
    setRecipientPhone(normalizedSelectedClientPhone);
    setRecipientNameError("");
    setRecipientEmailError("");
    setRecipientPhoneError("");
  }, [normalizedSelectedClientPhone, selectedProject?.id]);

  const shouldOpenGuidedStart =
    !isEditMode &&
    !searchParams.get("edit") &&
    !searchParams.get("projectId") &&
    !searchParams.get("type") &&
    !projectId;
  const [guidedStartOpen, setGuidedStartOpen] = useState(shouldOpenGuidedStart);
  const [projectContextOpen, setProjectContextOpen] = useState(false);

  useEffect(() => {
    setGuidedStartOpen(shouldOpenGuidedStart);
  }, [shouldOpenGuidedStart]);

  useEffect(() => {
    if (hasClientPrimaryEmail) {
      setSaveRecipientAsClientPrimary(false);
    }
  }, [hasClientPrimaryEmail]);

  useEffect(() => {
    if (approvalMethod === "onsite") {
      setRecipientNameError("");
      setRecipientEmailError("");
      setRecipientPhoneError("");
    }
  }, [approvalMethod]);

  useEffect(() => {
    if (!canUseSmsDelivery && deliveryMethod !== "EMAIL") {
      setDeliveryMethod("EMAIL");
    }
  }, [canUseSmsDelivery, deliveryMethod]);

  useEffect(() => {
    if (!deliveryUsesSms) {
      setSmsConsentConfirmed(false);
      setSmsConsentError("");
    }
  }, [deliveryUsesSms]);

  const setupSignatureCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.floor(rect.width * ratio);
    canvas.height = Math.floor(rect.height * ratio);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#111827";
  }, []);

  const redrawSignatureStrokes = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    for (const stroke of signatureStrokes) {
      if (stroke.length === 0) continue;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length; i += 1) {
        ctx.lineTo(stroke[i].x, stroke[i].y);
      }
      ctx.stroke();
    }
  }, [signatureStrokes]);

  useEffect(() => {
    if (!signatureModalOpen) return;
    setupSignatureCanvas();
    redrawSignatureStrokes();
    const onResize = () => {
      setupSignatureCanvas();
      redrawSignatureStrokes();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [signatureModalOpen, setupSignatureCanvas, redrawSignatureStrokes]);

  useEffect(() => {
    if (!signatureModalOpen) return;
    redrawSignatureStrokes();
  }, [signatureStrokes, signatureModalOpen, redrawSignatureStrokes]);

  const getPointFromEvent = (
    event: React.PointerEvent<HTMLCanvasElement>,
  ): SignaturePoint => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const onSignaturePointerDown = (
    event: React.PointerEvent<HTMLCanvasElement>,
  ) => {
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(event.pointerId);
    drawingRef.current = true;
    const point = getPointFromEvent(event);
    activeStrokeRef.current = [point];
    lastPointRef.current = point;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const onSignaturePointerMove = (
    event: React.PointerEvent<HTMLCanvasElement>,
  ) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const point = getPointFromEvent(event);
    activeStrokeRef.current.push(point);
    const prev = lastPointRef.current;
    const ctx = canvas.getContext("2d");
    if (ctx && prev) {
      ctx.beginPath();
      ctx.moveTo(prev.x, prev.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
    }
    lastPointRef.current = point;
  };

  const finishSignatureStroke = (
    event?: React.PointerEvent<HTMLCanvasElement>,
  ) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (event && canvasRef.current) {
      canvasRef.current.releasePointerCapture(event.pointerId);
    }
    if (activeStrokeRef.current.length > 0) {
      setSignatureStrokes((prev) => [...prev, activeStrokeRef.current]);
    }
    activeStrokeRef.current = [];
    lastPointRef.current = null;
  };

  const clearSignaturePad = () => {
    setSignatureStrokes([]);
    setSignatureError("");
    activeStrokeRef.current = [];
    lastPointRef.current = null;
    redrawSignatureStrokes();
  };

  const undoSignatureStroke = () => {
    setSignatureStrokes((prev) => prev.slice(0, -1));
    setSignatureError("");
  };

  const openSignatureModal = () => {
    if (capturedSignature) {
      setSignatureFormName(capturedSignature.signerName);
      setSignatureFormTitle(capturedSignature.signerTitle || "");
      setSignatureFormCompany(capturedSignature.signerCompany || "");
    } else {
      setSignatureFormName(signatureAutofillName);
      setSignatureFormTitle(signatureAutofillTitle);
      setSignatureFormCompany(signatureAutofillCompany);
    }
    setSignatureStrokes([]);
    setSignatureError("");
    setSignatureModalOpen(true);
  };

  const internalSubtotal = useMemo(
    () =>
      lineItems.reduce((sum, item) => sum + calculateLineItemTotal(item), 0),
    [lineItems],
  );
  const customerSubtotal = useMemo(
    () => Math.max(0, Number(customerPrice || 0) - Number(discountAmount || 0)),
    [customerPrice, discountAmount],
  );
  const subtotal = customerSubtotal;

  const total = subtotal + Number(taxAmount || 0);

  const handleCustomerPriceChange = (value: string) => {
    setCustomerPrice(value);
    setCustomerPriceManuallyOverridden(Boolean(value.trim()));
  };

  const internalEstimatePreviewRows = useMemo(
    () =>
      lineItems
        .filter((row) => row.description.trim().length > 0)
        .map((row) => {
          const payload = mapLineItemToPayload(row);
          return {
            id: row.id,
            description: row.description.trim(),
            category:
              lineItemOptions.find(
                (option) =>
                  option.value === normalizeLineItemCategory(row.category),
              )?.label ?? row.category,
            quantity: Number(payload.quantity || 0),
            unit: String(payload.unit ?? ""),
            unitCost: Number(payload.unitCost || 0),
            total: calculateLineItemTotal(row),
            note: row.details.trim(),
          };
        }),
    [lineItemOptions, lineItems],
  );

  const downloadInternalEstimateCsv = () => {
    const escapeCsv = (value: string | number) =>
      `"${String(value).replace(/"/g, '""')}"`;
    const rows = [
      ["Description", "Category", "Qty", "Unit", "Rate", "Total", "Internal note"],
      ...internalEstimatePreviewRows.map((row) => [
        row.description,
        row.category,
        row.quantity,
        row.unit,
        row.unitCost.toFixed(2),
        row.total.toFixed(2),
        row.note,
      ]),
    ];
    const csv = rows.map((row) => row.map(escapeCsv).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "internal-estimate.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (taxManuallyOverridden) return;
    const computedTax = (subtotal * defaultTaxRate) / 100;
    setTaxAmount(computedTax.toFixed(2));
  }, [subtotal, defaultTaxRate, taxManuallyOverridden]);

  useEffect(() => {
    if (!estimateCalculatorOpen) return;
    if (customerPriceManuallyOverridden) return;
    if (internalSubtotal <= 0) return;
    setCustomerPrice(internalSubtotal.toFixed(2));
  }, [
    customerPriceManuallyOverridden,
    estimateCalculatorOpen,
    internalSubtotal,
  ]);

  useEffect(() => {
    if (internalEstimateSaved) return;
    if (customerDisplayMode === "final_price_only") return;
    setCustomerDisplayMode("final_price_only");
  }, [customerDisplayMode, internalEstimateSaved]);

  const updateLine = (id: number, patch: Partial<LineItem>) => {
    setLineItems((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );
  };

  const updateLineDescription = (id: number, description: string) => {
    updateLine(id, {
      description,
      priceBookItemId: undefined,
      thumbnailPath: "",
      thumbnailUrl: undefined,
    });
  };

  const priceBookItems = priceBookQuery.data ?? [];

  const mapCategoryToPriceBookType = (category: LineItemCategory) => {
    if (category === "LABOR") return "LABOR";
    if (category === "MATERIAL") return "MATERIAL";
    if (category === "EQUIPMENT") return "EQUIPMENT";
    return "OTHER";
  };

  const mapPriceBookTypeToLineCategory = (
    type: PriceBookItem["type"],
  ): LineItemCategory => {
    if (type === "LABOR") return "LABOR";
    if (type === "MATERIAL") return "MATERIAL";
    if (type === "EQUIPMENT") return "EQUIPMENT";
    return "OTHER";
  };

  const getLineUnitOptions = (category: LineItemCategory) =>
    getPriceBookUnitOptionsForType(mapCategoryToPriceBookType(category));

  const resolveLineUnit = (category: LineItemCategory, unit?: string | null) =>
    resolvePriceBookUnitForType(mapCategoryToPriceBookType(category), unit);

  const changeLineCategory = (id: number, nextCategory: LineItemCategory) => {
    setLineItems((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const defaults = createLineItemFromDefaults(
          row.id,
          nextCategory,
          lineItemDefaultsByCategory,
        );
        const pricingMethod = row.pricingMethod;
        return {
          ...defaults,
          description: row.description,
          details: row.details,
          quantity: row.quantity,
          unit:
            pricingMethod === "FIXED"
              ? "fixed"
              : resolveLineUnit(nextCategory, row.unit),
          unitCost: row.unitCost,
          pricingMethod,
          fixedAmount: row.fixedAmount || row.unitCost,
          useUnit: pricingMethod !== "FIXED",
        };
      }),
    );
  };

  const setLinePricingMethod = (
    id: number,
    pricingMethod: LineItem["pricingMethod"],
  ) => {
    setLineItems((prev) =>
      prev.map((row) => {
        if (row.id !== id || row.pricingMethod === pricingMethod) return row;
        const category = normalizeLineItemCategory(row.category);
        if (pricingMethod === "FIXED") {
          const currentTotal = Math.abs(calculateLineItemTotal(row));
          const fixedAmount =
            currentTotal > 0
              ? currentTotal.toFixed(2)
              : row.fixedAmount || row.unitCost || "";
          return {
            ...row,
            pricingMethod: "FIXED",
            fixedAmount,
            quantity: "1",
            unit: "fixed",
            useUnit: false,
          };
        }

        const previousFlatCost = Number(row.fixedAmount || 0);
        return {
          ...row,
          pricingMethod: "HOURLY",
          quantity: Number(row.quantity || 0) > 0 ? row.quantity : "1",
          unit: resolveLineUnit(category, row.unit === "fixed" ? "" : row.unit),
          unitCost:
            row.unitCost ||
            (previousFlatCost > 0 ? previousFlatCost.toFixed(2) : ""),
          useUnit: true,
        };
      }),
    );
  };

  const addLine = () => {
    setLineItems((prev) => [
      ...prev,
      createLineItemFromDefaults(
        Date.now(),
        "LABOR",
        lineItemDefaultsByCategory,
      ),
    ]);
  };

  const removeLine = (id: number) => {
    setLineItems((prev) =>
      prev.length > 1 ? prev.filter((r) => r.id !== id) : prev,
    );
  };

  const getPriceBookMatches = (item: LineItem) => {
    const category = normalizeLineItemCategory(item.category);
    const query = item.description.trim().toLowerCase();
    return priceBookItems
      .filter((entry) => entry.type === mapCategoryToPriceBookType(category))
      .filter((entry) => {
        if (!query) return true;
        return (
          entry.name.toLowerCase().includes(query) ||
          (entry.description ?? "").toLowerCase().includes(query) ||
          (entry.unit ?? "").toLowerCase().includes(query)
        );
      })
      .slice(0, 6);
  };

  const applyPriceBookItem = (lineId: number, item: PriceBookItem) => {
    setLineItems((prev) =>
      prev.map((row) => {
        if (row.id !== lineId) return row;
        const category = mapPriceBookTypeToLineCategory(item.type);
        const base = {
          ...row,
          category,
          description: item.name,
          details: item.description ?? "",
          priceBookItemId: item.id,
          thumbnailPath: item.thumbnailPath ?? "",
          thumbnailUrl: item.thumbnailUrl ?? undefined,
        };

        if (item.pricingMode === "NO_UNITS") {
          return {
            ...base,
            pricingMethod: "FIXED",
            fixedAmount: String(item.defaultPrice ?? ""),
            quantity: "1",
            unit: "fixed",
            unitCost: String(item.defaultPrice ?? ""),
            useUnit: false,
          };
        }

        return {
          ...base,
          pricingMethod: "HOURLY",
          useUnit: true,
          quantity: "1",
          unit: resolveLineUnit(category, item.unit),
          unitCost: String(item.defaultPrice ?? ""),
          fixedAmount: "",
        };
      }),
    );
    setActivePriceBookLineId(null);
  };

  const saveInternalEstimate = () => {
    const hadSavedEstimate = internalEstimateSaved;
    const nextCustomerPrice =
      (!customerPrice.trim() || Number(customerPrice || 0) <= 0) &&
      internalSubtotal > 0
        ? internalSubtotal.toFixed(2)
        : customerPrice;
    setCustomerPrice(nextCustomerPrice);
    setCustomerPriceManuallyOverridden(Boolean(nextCustomerPrice.trim()));
    setInternalEstimateSaved(true);
    if (!hadSavedEstimate) {
      setCustomerDisplayMode("final_price_only");
    }
    setPricingMode("internal_estimate");
    setEstimateCalculatorOpen(false);
  };

  const openInternalEstimateCalculator = () => {
    if (!customerPriceManuallyOverridden && internalSubtotal > 0) {
      setCustomerPrice(internalSubtotal.toFixed(2));
    }
    setEstimateCalculatorOpen(true);
  };

  const renderPriceBookAutocomplete = (item: LineItem) => {
    if (activePriceBookLineId !== item.id) return null;
    const matches = getPriceBookMatches(item);
    if (!matches.length && !priceBookQuery.isFetching) return null;

    return (
      <div className="absolute left-0 right-0 top-full z-20 mt-1 rounded-md border bg-popover shadow-md">
        {priceBookQuery.isFetching && !priceBookItems.length ? (
          <div className="px-3 py-2 text-xs text-muted-foreground">
            Loading catalog...
          </div>
        ) : (
          <div className="max-h-72 overflow-y-auto p-1">
            {matches.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className="flex w-full items-start gap-2 rounded-md px-2 py-2 text-left hover:bg-secondary"
                onMouseDown={(event) => {
                  event.preventDefault();
                  applyPriceBookItem(item.id, entry);
                }}
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded border bg-muted/20">
                  {entry.thumbnailUrl ? (
                    <img
                      src={entry.thumbnailUrl}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-[10px] font-medium text-muted-foreground">
                      PB
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium text-foreground">
                      {entry.name}
                    </span>
                    <span className="shrink-0 text-xs font-semibold text-foreground">
                      ${Number(entry.defaultPrice ?? 0).toFixed(2)}
                    </span>
                  </div>
                  {entry.description ? (
                    <p className="truncate text-xs text-muted-foreground">
                      {entry.description}
                    </p>
                  ) : null}
                </div>
              </button>
            ))}
            {!matches.length ? (
              <div className="px-3 py-2 text-xs text-muted-foreground">
                No catalog matches.
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  const renderLineItemThumbnail = (item: LineItem) => {
    const src =
      item.thumbnailUrl ||
      (item.thumbnailPath?.trim() ? item.thumbnailPath.trim() : "");
    if (!src) return null;
    return (
      <div className="h-8 w-8 shrink-0 overflow-hidden rounded border bg-muted/20">
        <img src={src} alt="" className="h-full w-full object-cover" />
      </div>
    );
  };

  const detectAttachmentKind = (file: File) => {
    const mime = file.type.toLowerCase();
    const isImage =
      ALLOWED_IMAGE_MIME.has(mime) || IMAGE_EXT_RE.test(file.name);
    const isPdf = mime === "application/pdf" || PDF_EXT_RE.test(file.name);
    if (isImage) return "image";
    if (isPdf) return "pdf";
    return null;
  };

  const compressImageIfNeeded = (file: File) =>
    new Promise<File>((resolve) => {
      const tooLargeBySize = file.size > 1.5 * 1024 * 1024;
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = async () => {
          const maxDimension = 1920;
          const biggerSide = Math.max(img.width, img.height);
          const needsResize = biggerSide > 2000;
          if (!tooLargeBySize && !needsResize) {
            resolve(file);
            return;
          }
          const ratio = Math.min(1, maxDimension / biggerSide);
          const width = Math.max(1, Math.round(img.width * ratio));
          const height = Math.max(1, Math.round(img.height * ratio));
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            resolve(file);
            return;
          }
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                resolve(file);
                return;
              }
              const nextName = file.name.replace(/\.[^.]+$/, "") + ".jpg";
              resolve(new File([blob], nextName, { type: "image/jpeg" }));
            },
            "image/jpeg",
            0.82,
          );
        };
        img.onerror = () => resolve(file);
        img.src = String(reader.result ?? "");
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });

  const addAttachmentFiles = async (files: File[]) => {
    if (!files.length) return;
    if (user?.currentPlan === "STARTER" && attachments.length >= 1) {
      toast({
        title: t("createTicket.toasts.starterAttachmentLimitTitle"),
        description: t("createTicket.toasts.starterAttachmentLimitDescription"),
        variant: "destructive",
      });
      return;
    }

    const filesToProcess =
      user?.currentPlan === "STARTER" && files.length > 1
        ? files.slice(0, 1)
        : files;

    if (user?.currentPlan === "STARTER" && files.length > 1) {
      toast({
        title: t("createTicket.toasts.starterAttachmentLimitTitle"),
        description: t("createTicket.toasts.starterAttachmentLimitSingleFile"),
        variant: "destructive",
      });
    }

    const nextItems: AttachmentItem[] = [];
    for (const originalFile of filesToProcess) {
      const kind = detectAttachmentKind(originalFile);
      if (!kind) {
        toast({
          title: t("createTicket.toasts.unsupportedFileTitle"),
          description: t("createTicket.toasts.unsupportedFileDescription", {
            fileName: originalFile.name,
          }),
          variant: "destructive",
        });
        continue;
      }
      const maxBytes = kind === "pdf" ? MAX_PDF_BYTES : MAX_IMAGE_BYTES;
      if (originalFile.size > maxBytes) {
        toast({
          title: t("createTicket.toasts.fileTooLargeTitle"),
          description:
            kind === "pdf"
              ? t("createTicket.toasts.pdfTooLargeDescription", {
                  fileName: originalFile.name,
                })
              : t("createTicket.toasts.imageTooLargeDescription", {
                  fileName: originalFile.name,
                }),
          variant: "destructive",
        });
        continue;
      }

      const finalFile =
        kind === "image"
          ? await compressImageIfNeeded(originalFile)
          : originalFile;
      const previewUrl =
        kind === "image" ? URL.createObjectURL(finalFile) : undefined;
      nextItems.push({
        id: Date.now() + Math.random(),
        name: finalFile.name,
        size: finalFile.size,
        mimeType:
          finalFile.type ||
          originalFile.type ||
          (kind === "pdf" ? "application/pdf" : "image/jpeg"),
        file: finalFile,
        status: "ready",
        progress: 0,
        previewUrl,
      });
    }

    if (nextItems.length) {
      setAttachments((prev) => [...prev, ...nextItems]);
    }
  };

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  useEffect(() => {
    return () => {
      attachmentsRef.current.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
      stopCameraStream();
    };
  }, []);

  const stopCameraStream = () => {
    const stream = cameraStreamRef.current;
    if (!stream) return;
    stream.getTracks().forEach((track) => track.stop());
    cameraStreamRef.current = null;
  };

  const openCamera = async () => {
    if (starterAttachmentLimitReached) {
      toast({
        title: t("createTicket.toasts.starterAttachmentLimitTitle"),
        description: t("createTicket.toasts.starterAttachmentLimitDescription"),
        variant: "destructive",
      });
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      toast({
        title: t("createTicket.toasts.cameraNotSupportedTitle"),
        description: t("createTicket.toasts.cameraNotSupportedDescription"),
        variant: "destructive",
      });
      return;
    }
    setCameraError("");
    setCameraOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
        },
        audio: false,
      });
      cameraStreamRef.current = stream;
      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = stream;
        await cameraVideoRef.current.play();
      }
    } catch (e) {
      setCameraError(
        e instanceof Error
          ? e.message
          : t("createTicket.toasts.unableToAccessCamera"),
      );
      toast({
        title: t("createTicket.toasts.cameraAccessFailedTitle"),
        description:
          e instanceof Error
            ? e.message
            : t("createTicket.toasts.unableToAccessCamera"),
        variant: "destructive",
      });
    }
  };

  const closeCamera = () => {
    stopCameraStream();
    setCameraOpen(false);
    setCameraError("");
    setCapturingPhoto(false);
  };

  const capturePhotoFromCamera = async () => {
    const video = cameraVideoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      setCameraError(t("createTicket.toasts.cameraNotReadyYet"));
      return;
    }
    setCapturingPhoto(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        throw new Error(t("createTicket.toasts.couldNotCapturePhoto"));
      }
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((result) => resolve(result), "image/jpeg", 0.9),
      );
      if (!blob) {
        throw new Error(t("createTicket.toasts.couldNotCapturePhoto"));
      }
      const photoFile = new File([blob], `photo-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });
      await addAttachmentFiles([photoFile]);
      closeCamera();
    } catch (e) {
      setCameraError(
        e instanceof Error ? e.message : t("createTicket.toasts.captureFailed"),
      );
    } finally {
      setCapturingPhoto(false);
    }
  };

  const removeAttachment = async (id: number) => {
    const target = attachments.find((a) => a.id === id);
    if (!target) return;
    if (target.status === "uploading") return;

    if (target.uploaded?.id) {
      try {
        await deleteAttachment(target.uploaded.id);
      } catch (e) {
        toast({
          title: t("ticketDetail.toasts.removeFailedTitle"),
          description:
            e instanceof Error
              ? e.message
              : t("ticketDetail.toasts.removeFailedDescription"),
          variant: "destructive",
        });
        return;
      }
    }

    if (target.previewUrl) {
      URL.revokeObjectURL(target.previewUrl);
    }
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const uploadAttachmentItem = async (ticketId: string, itemId: number) => {
    const target = attachments.find((a) => a.id === itemId);
    if (!target || target.status === "uploaded") return true;

    setAttachments((prev) =>
      prev.map((row) =>
        row.id === itemId
          ? { ...row, status: "uploading", progress: 0, error: undefined }
          : row,
      ),
    );

    const tryOnce = async () =>
      uploadTicketAttachment({
        ticketId,
        file: target.file,
        onProgress: (progress) => {
          setAttachments((prev) =>
            prev.map((row) => (row.id === itemId ? { ...row, progress } : row)),
          );
        },
      });

    try {
      const uploaded = await tryOnce();
      setAttachments((prev) =>
        prev.map((row) =>
          row.id === itemId
            ? { ...row, status: "uploaded", progress: 100, uploaded }
            : row,
        ),
      );
      return true;
    } catch {
      try {
        const uploaded = await tryOnce();
        setAttachments((prev) =>
          prev.map((row) =>
            row.id === itemId
              ? { ...row, status: "uploaded", progress: 100, uploaded }
              : row,
          ),
        );
        return true;
      } catch (e) {
        setAttachments((prev) =>
          prev.map((row) =>
            row.id === itemId
              ? {
                  ...row,
                  status: "failed",
                  progress: 0,
                  error:
                    e instanceof Error
                      ? e.message
                      : t("ticketDetail.toasts.uploadFailedTitle"),
                }
              : row,
          ),
        );
        return false;
      }
    }
  };

  const uploadPendingAttachments = async (ticketId: string) => {
    setProcessingUploads(true);
    try {
      const pendingIds = attachments
        .filter((item) => item.status === "ready" || item.status === "failed")
        .map((item) => item.id);
      let failed = 0;
      for (const id of pendingIds) {
        const ok = await uploadAttachmentItem(ticketId, id);
        if (!ok) failed += 1;
      }
      return failed;
    } finally {
      setProcessingUploads(false);
    }
  };

  const retryAttachment = async (id: number) => {
    if (!createdTicketForUploads) {
      toast({
        title: t("createTicket.toasts.saveTicketFirstTitle"),
        description: t("createTicket.toasts.saveTicketFirstDescription"),
      });
      return;
    }
    setProcessingUploads(true);
    try {
      const ok = await uploadAttachmentItem(createdTicketForUploads.id, id);
      if (!ok) {
        toast({
          title: t("createTicket.toasts.retryFailedTitle"),
          description: t("createTicket.toasts.retryFailedDescription"),
          variant: "destructive",
        });
      }
    } finally {
      setProcessingUploads(false);
    }
  };

  const openAddProjectModal = () => {
    setProjectForm({
      name: "",
      code: nextProjectCode(),
      clientId: "",
      jobSiteAddress: "",
    });
    setAddProjectOpen(true);
  };

  const addClientForProject = async () => {
    if (!newProjectClientName.trim()) {
      toast({
        title: t("createTicket.toasts.clientNameRequired"),
        variant: "destructive",
      });
      return;
    }
    setCreatingProjectClient(true);
    try {
      const client = await createClient({
        name: newProjectClientName.trim(),
        email: newProjectClientEmail.trim() || undefined,
        type: "General Contractor",
      });
      const nextClients = await fetchClients();
      setClients(nextClients);
      setProjectForm((prev) => ({ ...prev, clientId: client.id }));
      setNewProjectClientName("");
      setNewProjectClientEmail("");
      setAddProjectClientOpen(false);
      toast({ title: t("createTicket.toasts.clientCreated") });
    } catch (e) {
      toast({
        title: t("createTicket.toasts.createClientFailedTitle"),
        description:
          e instanceof Error
            ? e.message
            : t("createTicket.toasts.createClientFailedDescription"),
        variant: "destructive",
      });
    } finally {
      setCreatingProjectClient(false);
    }
  };

  const submitProject = async () => {
    if (!projectForm.name.trim()) {
      toast({
        title: t("createTicket.toasts.projectNameRequiredTitle"),
        description: t("createTicket.toasts.projectNameRequiredDescription"),
        variant: "destructive",
      });
      return;
    }
    if (!projectForm.clientId) {
      toast({
        title: t("createTicket.toasts.clientRequiredTitle"),
        description: t("createTicket.toasts.clientRequiredDescription"),
        variant: "destructive",
      });
      return;
    }
    setProjectSaving(true);
    try {
      const created = await createProject({
        name: projectForm.name.trim(),
        code: projectForm.code.trim() || nextProjectCode(),
        clientId: projectForm.clientId,
        jobSiteAddress: projectForm.jobSiteAddress.trim() || undefined,
        status: "ACTIVE",
      });
      await invalidateProjectData(queryClient);
      setProjectId(created.id);
      setAddProjectOpen(false);
      toast({ title: t("createTicket.toasts.projectCreated") });
    } catch (e) {
      toast({
        title: t("common.createFailed"),
        description:
          e instanceof Error
            ? e.message
            : t("createTicket.toasts.unableToSaveProject"),
        variant: "destructive",
      });
    } finally {
      setProjectSaving(false);
    }
  };

  const buildPricingMetadata = () =>
    JSON.stringify({
      pricingVersion: 1,
      pricingMode,
      customerDisplayMode: internalEstimateSaved
        ? customerDisplayMode
        : "final_price_only",
      customerPrice: Number(customerPrice || 0),
      discountAmount: Number(discountAmount || 0),
      taxAmount: Number(taxAmount || 0),
      finalAmount: total,
      changeOrder:
        ticketType === "co"
          ? {
              reasonForChange: changeOrderReason,
              reasonForChangeLabel: changeOrderReasonLabel,
              changeType: changeOrderType,
              changeTypeLabel: changeOrderTypeLabel,
              currentApprovedEstimate:
                changeOrderContractSummary.approvedEstimateTotal,
              existingApprovedChangeOrderAdditions:
                changeOrderContractSummary.approvedChangeOrderAdditionsTotal,
              existingApprovedChangeOrderDeductions:
                changeOrderContractSummary.approvedChangeOrderDeductionsTotal,
              currentContractTotal:
                changeOrderContractSummary.currentContractTotal,
              thisChangeOrderAmount: changeOrderAmount,
              projectedContractTotal,
            }
          : undefined,
      extraWork:
        ticketType === "ew"
          ? {
              reason: extraWorkReason,
              reasonLabel: extraWorkReasonLabel,
            }
          : undefined,
      internalEstimate: internalEstimateSaved
        ? {
            rows: lineItems
              .filter((row) => row.description.trim().length > 0)
              .map((row) => {
                const payload = mapLineItemToPayload(row);
                return {
                  id: String(row.id),
                  category: normalizePricingCategory(row.category),
                  description: row.description.trim(),
                  quantity: Number(payload.quantity || 0),
                  unit: normalizePricingUnit(payload.unit),
                  unitCost: Number(payload.unitCost || 0),
                  pricingMethod:
                    row.pricingMethod === "FIXED"
                      ? "flat_cost"
                      : "per_unit",
                  markup: 0,
                  lineTotal: calculateLineItemTotal(row),
                  internalNote: row.details.trim() || undefined,
                  priceBookItemId: row.priceBookItemId,
                  thumbnailPath: row.thumbnailPath.trim() || undefined,
                  visibleToCustomer: false,
                };
              }),
            internalSubtotal,
            suggestedCustomerPrice: internalSubtotal,
            customerFacingFinalPrice: total,
          }
        : null,
    });

  const buildPayload = (
    status: "DRAFT" | "PENDING_APPROVAL" | "APPROVED",
    signatureOverride?: CapturedSignature | null,
  ) => ({
    projectId,
    clientId: selectedProject?.clientId || undefined,
    type: mapUiTypeToApi(ticketType),
    title,
    description: description || undefined,
    showDescriptionToCustomer:
      ticketType === "tm" ? showDescriptionToCustomer : true,
    showAttachmentsToCustomer,
    reasonImpact:
      ticketType === "co"
        ? description || undefined
        : ticketType === "ew"
          ? description || undefined
          : undefined,
    delayImpact: undefined,
    delayDays: undefined,
    requestSource: ticketType === "ew" ? description || undefined : undefined,
    internalNotes: buildPricingMetadata(),
    taxAmount:
      ticketType === "co" && changeOrderType === "no_cost_change"
        ? 0
        : Number(taxAmount || 0),
    status,
    signatureStatus:
      (signatureOverride ?? capturedSignature) ? "CAPTURED" : "PENDING",
    initialSignature:
      (signatureOverride ?? capturedSignature)
        ? {
            signerName: (signatureOverride ?? capturedSignature)!.signerName,
            signerTitle: (signatureOverride ?? capturedSignature)!.signerTitle,
            signerCompany: (signatureOverride ?? capturedSignature)!
              .signerCompany,
            signatureDataUrl: (signatureOverride ?? capturedSignature)!
              .imageDataUrl,
            timestamp: (signatureOverride ?? capturedSignature)!.capturedAt,
          }
        : undefined,
    lineItems:
      subtotal > 0 &&
      !(ticketType === "co" && changeOrderType === "no_cost_change")
        ? [
            {
              direction:
                ticketType === "co" && changeOrderType === "credit_deduct"
                  ? "DEDUCT"
                  : "ADD",
              category: "OTHER",
              description:
                pricingMode === "internal_estimate"
                  ? "Customer-facing final price"
                  : "Customer price",
              details:
                Number(discountAmount || 0) > 0
                  ? `Discount applied: $${Number(discountAmount || 0).toFixed(2)}`
                  : undefined,
              quantity: 1,
              unit: "fixed",
              unitCost: subtotal,
            },
          ]
        : [],
  });

  const hasMeaningfulDraftContent = useCallback(() => {
    if (!projectId.trim()) return false;
    if (titleDirty && title.trim()) return true;
    if (description.trim()) return true;
    if (customerPrice.trim() || discountAmount.trim()) return true;
    if (message.trim()) return true;
    if (attachments.length > 0) return true;
    if (capturedSignature) return true;
    if (taxManuallyOverridden && Number(taxAmount || 0) !== 0) return true;
    if (recipientNameTouched && recipientName.trim()) return true;
    if (recipientEmailTouched && recipientEmail.trim()) return true;
    if (recipientPhoneTouched && recipientPhone.trim()) return true;
    return lineItems.some(
      (row) =>
        row.description.trim() ||
        row.details.trim() ||
        row.thumbnailPath.trim() ||
        row.thumbnailUrl?.trim() ||
        Number(row.quantity || 0) > 0 ||
        Number(row.unitCost || 0) > 0 ||
        Number(row.fixedAmount || 0) > 0,
    );
  }, [
    attachments.length,
    capturedSignature,
    customerPrice,
    description,
    discountAmount,
    lineItems,
    message,
    projectId,
    recipientEmail,
    recipientEmailTouched,
    recipientName,
    recipientNameTouched,
    recipientPhone,
    recipientPhoneTouched,
    taxAmount,
    taxManuallyOverridden,
    title,
    titleDirty,
  ]);
  const firstLineItemMissingDescriptionIndex = useMemo(
    () =>
      lineItems.findIndex((row) =>
        lineItemHasMeaningfulContentWithoutDescription(row),
      ),
    [lineItems],
  );

  const buildAutosaveSnapshot = useCallback(
    () =>
      JSON.stringify({
        projectId,
        ticketType,
        title,
        titleDirty,
        description,
        changeOrderReason,
        changeOrderType,
        extraWorkReason,
        showDescriptionToCustomer,
        showAttachmentsToCustomer,
        pricingMode,
        customerPrice,
        discountAmount,
        customerDisplayMode,
        internalEstimateSaved,
        taxAmount,
        taxManuallyOverridden,
        approvalMethod,
        deliveryMethod,
        recipientName,
        recipientEmail,
        recipientPhone,
        message,
        reminderCadenceOption,
        customReminderCadenceDays,
        lineItems,
        capturedSignature,
        attachments: attachments.map((item) => ({
          id: item.id,
          name: item.name,
          status: item.status,
          uploadedId: item.uploaded?.id ?? null,
        })),
      }),
    [
      approvalMethod,
      attachments,
      capturedSignature,
      changeOrderReason,
      changeOrderType,
      customerDisplayMode,
      customerPrice,
      customReminderCadenceDays,
      deliveryMethod,
      description,
      discountAmount,
      extraWorkReason,
      internalEstimateSaved,
      lineItems,
      message,
      pricingMode,
      projectId,
      recipientEmail,
      recipientName,
      recipientPhone,
      reminderCadenceOption,
      showAttachmentsToCustomer,
      showDescriptionToCustomer,
      taxAmount,
      taxManuallyOverridden,
      ticketType,
      title,
      titleDirty,
    ],
  );

  const validateBase = () => {
    if (!projectId.trim() || !title.trim()) {
      toast({
        title: t("createTicket.toasts.requiredFieldsMissingTitle"),
        description: t("createTicket.toasts.requiredFieldsMissingDescription"),
        variant: "destructive",
      });
      return false;
    }
    if (firstLineItemMissingDescriptionIndex >= 0) {
      toast({
        title: t("createTicket.toasts.lineItemDescriptionRequiredTitle"),
        description: t(
          "createTicket.toasts.lineItemDescriptionRequiredDescription",
          {
            index: firstLineItemMissingDescriptionIndex + 1,
          },
        ),
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const persistTicket = async (
    status: "DRAFT" | "PENDING_APPROVAL" | "APPROVED",
    signatureOverride?: CapturedSignature | null,
  ) => {
    let targetId =
      editTicketId ||
      persistedTicketIdRef.current ||
      createdTicketForUploads?.id ||
      null;

    if (!targetId && inFlightCreateTicketRef.current) {
      const inFlightTicket = await inFlightCreateTicketRef.current;
      targetId = inFlightTicket.id;
    }

    const request = targetId
      ? apiRequest<CreateTicketResponse>(`/tickets/${targetId}`, {
          method: "PATCH",
          body: buildPayload(status, signatureOverride),
        })
      : apiRequest<CreateTicketResponse>("/tickets", {
          method: "POST",
          body: buildPayload(status, signatureOverride),
        });

    if (!targetId) {
      inFlightCreateTicketRef.current = request;
    }

    try {
      const ticket = await request;
      persistedTicketIdRef.current = ticket.id;
      setCreatedTicketForUploads(ticket);
      return ticket;
    } finally {
      if (!targetId && inFlightCreateTicketRef.current === request) {
        inFlightCreateTicketRef.current = null;
      }
    }
  };

  const persistDraftSilently = useCallback(async () => {
    if (isEditMode) return;
    if (!hydratedDraftRef.current) return;
    if (
      loadingEdit ||
      savingDraft ||
      savingSend ||
      processingUploads ||
      finalizingOnsiteApproval
    )
      return;
    if (!hasMeaningfulDraftContent()) return;
    if (firstLineItemMissingDescriptionIndex >= 0) return;

    const snapshot = buildAutosaveSnapshot();
    if (
      snapshot === lastAutosavedSnapshotRef.current ||
      autosaveInFlightRef.current
    ) {
      return;
    }

    autosaveInFlightRef.current = true;
    try {
      await persistTicket("DRAFT");
      await invalidateTicketData(queryClient);
      lastAutosavedSnapshotRef.current = snapshot;
    } catch {
      // Silent by design. This path is a safety net, not an interruptive workflow.
    } finally {
      autosaveInFlightRef.current = false;
    }
  }, [
    buildAutosaveSnapshot,
    finalizingOnsiteApproval,
    firstLineItemMissingDescriptionIndex,
    hasMeaningfulDraftContent,
    isEditMode,
    loadingEdit,
    processingUploads,
    queryClient,
    savingDraft,
    savingSend,
  ]);

  useEffect(() => {
    if (isEditMode || !hydratedDraftRef.current) return;
    if (!hasMeaningfulDraftContent()) return;

    if (autosaveTimeoutRef.current) {
      window.clearTimeout(autosaveTimeoutRef.current);
    }

    autosaveTimeoutRef.current = window.setTimeout(() => {
      void persistDraftSilently();
    }, 1200);

    return () => {
      if (autosaveTimeoutRef.current) {
        window.clearTimeout(autosaveTimeoutRef.current);
        autosaveTimeoutRef.current = null;
      }
    };
  }, [
    approvalMethod,
    attachments,
    capturedSignature,
    customerDisplayMode,
    customerPrice,
    customReminderCadenceDays,
    deliveryMethod,
    description,
    discountAmount,
    hasMeaningfulDraftContent,
    internalEstimateSaved,
    isEditMode,
    lineItems,
    message,
    persistDraftSilently,
    pricingMode,
    projectId,
    recipientEmail,
    recipientName,
    recipientPhone,
    reminderCadenceOption,
    showAttachmentsToCustomer,
    showDescriptionToCustomer,
    taxAmount,
    taxManuallyOverridden,
    ticketType,
    title,
    titleDirty,
  ]);

  useEffect(() => {
    return () => {
      if (skipAutosaveOnUnmountRef.current) {
        return;
      }
      void persistDraftSilently();
    };
  }, [persistDraftSilently]);

  const saveDraft = async () => {
    if (!validateBase()) return;

    setSavingDraft(true);
    try {
      const nextStatus =
        approvalMethod === "onsite" && capturedSignature ? "APPROVED" : "DRAFT";
      const ticket = await persistTicket(nextStatus);

      const failedUploads = attachments.length
        ? await uploadPendingAttachments(ticket.id)
        : 0;
      if (failedUploads > 0) {
        toast({
          title: t("createTicket.toasts.someAttachmentsFailedTitle"),
          description: t(
            "createTicket.toasts.someAttachmentsFailedDescription",
            { count: failedUploads },
          ),
          variant: "destructive",
        });
        return;
      }

      await invalidateTicketData(queryClient);
      toast({
        title:
          nextStatus === "APPROVED"
            ? t("createTicket.toasts.approvedOnSiteTitle")
            : isEditMode
              ? t("createTicket.toasts.ticketUpdatedTitle")
              : t("createTicket.toasts.draftSavedTitle"),
        description:
          nextStatus === "APPROVED"
            ? t("createTicket.toasts.approvedOnSiteDescription", {
                ticketNumber: ticket.ticketNumber,
              })
            : isEditMode
              ? t("createTicket.toasts.ticketUpdatedDescription", {
                  ticketNumber: ticket.ticketNumber,
                })
              : t("createTicket.toasts.ticketCreatedDescription", {
                  ticketNumber: ticket.ticketNumber,
                }),
      });
      clearCurrentDraft();
      navigate(`/tickets/${ticket.id}`);
    } catch (e) {
      toast({
        title: t("createTicket.toasts.saveFailedTitle"),
        description:
          e instanceof Error
            ? e.message
            : t("createTicket.toasts.unableToSaveDraft"),
        variant: "destructive",
      });
    } finally {
      setSavingDraft(false);
    }
  };

  const saveToDraftOnly = async () => {
    if (!validateBase()) return;

    setSavingDraft(true);
    try {
      const ticket = await persistTicket("DRAFT");

      const failedUploads = attachments.length
        ? await uploadPendingAttachments(ticket.id)
        : 0;
      if (failedUploads > 0) {
        toast({
          title: t("createTicket.toasts.someAttachmentsFailedTitle"),
          description: t(
            "createTicket.toasts.someAttachmentsFailedDescription",
            { count: failedUploads },
          ),
          variant: "destructive",
        });
        return;
      }

      await invalidateTicketData(queryClient);
      toast({
        title: isEditMode
          ? t("createTicket.toasts.ticketUpdatedTitle")
          : t("createTicket.toasts.savedToDraftTitle"),
        description: isEditMode
          ? t("createTicket.toasts.ticketUpdatedDescription", {
              ticketNumber: ticket.ticketNumber,
            })
          : t("createTicket.toasts.ticketInDraftDescription", {
              ticketNumber: ticket.ticketNumber,
            }),
      });
      clearCurrentDraft();
      navigate(`/tickets/${ticket.id}`);
    } catch (e) {
      toast({
        title: t("createTicket.toasts.saveFailedTitle"),
        description:
          e instanceof Error
            ? e.message
            : t("createTicket.toasts.unableToSaveDraft"),
        variant: "destructive",
      });
    } finally {
      setSavingDraft(false);
    }
  };

  const saveAndSend = async () => {
    if (!validateBase()) return;

    if (approvalMethod !== "remote") {
      await saveDraft();
      return;
    }

    const missingName = !recipientName.trim();
    const requiresEmail =
      deliveryMethod === "EMAIL" || deliveryMethod === "EMAIL_AND_SMS";
    const requiresPhone =
      deliveryMethod === "SMS" || deliveryMethod === "EMAIL_AND_SMS";
    const invalidEmail =
      requiresEmail &&
      (!effectiveRecipientEmail.includes("@") ||
        ccRecipientEmails.some((email) => !email.includes("@")));
    const normalizedRecipientPhone = normalizePhoneNumber(recipientPhone);
    const invalidPhone = requiresPhone && !normalizedRecipientPhone;
    const missingSmsConsent = requiresPhone && !smsConsentConfirmed;
    setRecipientNameError(
      missingName ? t("createTicket.toasts.recipientNameRequired") : "",
    );
    setRecipientEmailError(
      invalidEmail ? t("createTicket.toasts.recipientEmailRequired") : "",
    );
    setRecipientPhoneError(
      invalidPhone ? t("createTicket.toasts.recipientPhoneRequired") : "",
    );
    setSmsConsentError(
      missingSmsConsent ? SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE : "",
    );

    if (missingName || invalidEmail || invalidPhone || missingSmsConsent) {
      toast({
        title: t("createTicket.toasts.cannotSendForApprovalTitle"),
        description: missingSmsConsent
          ? SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE
          : t("createTicket.toasts.cannotSendForApprovalDescription"),
        variant: "destructive",
      });
      return;
    }

    let reminderCadenceDays: number | undefined;
    if (workspaceAutoRemindersEnabled) {
      if (reminderCadenceOption === "custom") {
        const parsed = Number(customReminderCadenceDays);
        if (!Number.isInteger(parsed) || parsed < 1) {
          toast({
            title: t("ticketDetail.toasts.invalidReminderCadenceTitle"),
            description: t(
              "ticketDetail.toasts.invalidReminderCadenceDescription",
            ),
            variant: "destructive",
          });
          return;
        }
        reminderCadenceDays = parsed;
      } else {
        reminderCadenceDays = Number(reminderCadenceOption);
      }
    }

    setSavingSend(true);
    try {
      if (!capturedSignature) {
        toast({
          title: t("createTicket.toasts.signatureMissingOptionalTitle"),
          description: t(
            "createTicket.toasts.signatureMissingOptionalDescription",
          ),
        });
      }

      const ticket = await persistTicket("DRAFT");

      const failedUploads = attachments.length
        ? await uploadPendingAttachments(ticket.id)
        : 0;
      if (failedUploads > 0) {
        toast({
          title: t("createTicket.toasts.attachmentsUploadFailedTitle"),
          description: t(
            "createTicket.toasts.attachmentsUploadFailedDescription",
            { count: failedUploads },
          ),
          variant: "destructive",
        });
        return;
      }

      if (
        saveRecipientAsClientPrimary &&
        selectedProject?.client?.id &&
        !hasClientPrimaryEmail &&
        effectiveRecipientEmail
      ) {
        await updateClient(selectedProject.client.id, {
          email: effectiveRecipientEmail,
        });
        await invalidateClientData(queryClient);
      }

      await apiRequest(`/tickets/${ticket.id}/send-approval`, {
        method: "POST",
        body: {
          deliveryMethod,
          recipientName: recipientName.trim(),
          recipientEmail: requiresEmail ? effectiveRecipientEmail : undefined,
          recipientPhone: requiresPhone ? normalizedRecipientPhone : undefined,
          smsConsentConfirmed: requiresPhone ? smsConsentConfirmed : undefined,
          ccEmails: requiresEmail ? ccRecipientEmails : [],
          message: requiresEmail ? message : "",
          reminderCadenceDays,
        },
      });
      await invalidateTicketData(queryClient);
      window.dispatchEvent(new Event("workscope:notifications-refresh"));

      toast({
        title: t("ticketDetail.toasts.sentForApprovalTitle"),
        description: isEditMode
          ? t("createTicket.toasts.ticketUpdatedAndSentDescription", {
              ticketNumber: ticket.ticketNumber,
            })
          : t("createTicket.toasts.ticketCreatedSuccessfullyDescription", {
              ticketNumber: ticket.ticketNumber,
            }),
      });
      clearCurrentDraft();
      navigate(`/tickets/${ticket.id}`);
    } catch (e) {
      toast({
        title: t("createTicket.toasts.submitFailedTitle"),
        description:
          e instanceof Error
            ? e.message
            : t("createTicket.toasts.unableToCreateTicket"),
        variant: "destructive",
      });
    } finally {
      setSavingSend(false);
    }
  };

  const handlePrimaryAction = async () => {
    if (approvalMethod === "remote") {
      if (
        ticketType === "co" &&
        changeOrderType !== "no_cost_change" &&
        Number(customerPrice || 0) <= 0
      ) {
        toast({
          title: "Change order amount required",
          description:
            "Enter a positive customer price for add-cost or credit change orders.",
          variant: "destructive",
        });
        return;
      }
      if (total <= 0) {
        if (ticketType === "co" && changeOrderType === "no_cost_change") {
          await saveAndSend();
          return;
        }
        toast({
          title: "Customer price required",
          description:
            "Customer-facing final price must be greater than 0 before sending.",
          variant: "destructive",
        });
        return;
      }
      await saveAndSend();
      return;
    }
    await saveDraft();
  };

  const openOnsitePdfPreview = async () => {
    if (!validateBase()) return;
    setPreparingOnsitePreview(true);
    try {
      const ticket = await persistTicket("DRAFT");
      const failedUploads = attachments.length
        ? await uploadPendingAttachments(ticket.id)
        : 0;
      if (failedUploads > 0) {
        toast({
          title: t("createTicket.toasts.attachmentsUploadFailedTitle"),
          description: t(
            "createTicket.toasts.attachmentsUploadFailedPdfDescription",
            { count: failedUploads },
          ),
          variant: "destructive",
        });
        return;
      }

      const pdfMeta = await apiRequest<{ url?: string | null }>(
        `/billing-exports/tickets/${ticket.id}/pdf`,
        {
          method: "GET",
        },
      );

      if (!pdfMeta?.url) {
        throw new Error(t("createTicket.toasts.pdfPreviewUnavailableNow"));
      }

      await invalidateTicketData(queryClient);
      const previewUrl = new URL(pdfMeta.url, window.location.origin);
      previewUrl.searchParams.set("preview", String(Date.now()));
      setOnsitePreviewUrl(previewUrl.toString());
      setOnsitePreviewOpen(true);
    } catch (e) {
      toast({
        title: t("createTicket.toasts.previewFailedTitle"),
        description:
          e instanceof Error
            ? e.message
            : t("createTicket.toasts.unableToPreparePdfPreview"),
        variant: "destructive",
      });
    } finally {
      setPreparingOnsitePreview(false);
    }
  };

  const finalizeOnsiteApproval = async (signature: CapturedSignature) => {
    setFinalizingOnsiteApproval(true);
    try {
      const ticket = await persistTicket("APPROVED", signature);
      const failedUploads = attachments.length
        ? await uploadPendingAttachments(ticket.id)
        : 0;
      if (failedUploads > 0) {
        toast({
          title: t("createTicket.toasts.someAttachmentsFailedTitle"),
          description: t(
            "createTicket.toasts.someAttachmentsFailedDescription",
            { count: failedUploads },
          ),
          variant: "destructive",
        });
        return;
      }

      await apiRequest(`/billing-exports/tickets/${ticket.id}/pdf`, {
        method: "GET",
      });

      await invalidateTicketData(queryClient);
      setOnsitePreviewOpen(false);
      setSignatureRequestedFromPreview(false);
      toast({
        title: t("createTicket.toasts.approvedOnSiteTitle"),
        description: t("createTicket.toasts.approvedAndSignedPdfGenerated", {
          ticketNumber: ticket.ticketNumber,
        }),
      });
      clearCurrentDraft();
      navigate(`/tickets/${ticket.id}`);
    } catch (e) {
      toast({
        title: t("createTicket.toasts.onSiteApprovalFailedTitle"),
        description:
          e instanceof Error
            ? e.message
            : t("createTicket.toasts.unableToFinalizeSignedTicket"),
        variant: "destructive",
      });
    } finally {
      setFinalizingOnsiteApproval(false);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in pb-24 [&_h1]:text-[#0C1F4F] [&_h2]:text-[#0C1F4F] [&_h3]:text-[#0C1F4F] [&_label]:!text-[#0C1F4F]">
      <GuidedTicketStart
        open={guidedStartOpen}
        onOpenChange={setGuidedStartOpen}
      />
      <div className="flex items-start gap-3">
        <button
          className="mt-0.5 rounded-md p-2 hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => {
            clearCurrentDraft();
            navigate(-1);
          }}
          disabled={isSubmitting}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold">
            {isEditMode
              ? t("createTicket.editTicket")
              : t("createTicket.newTicket")}
          </h1>
          {selectedProject && projectId ? (
            <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
              <span className="inline-flex rounded-full border border-accent/30 bg-accent/10 px-2 py-0.5 text-xs font-semibold text-[#0C1F4F]">
                {selectedTicketTypeLabel}
              </span>
              <span className="min-w-0 truncate font-semibold text-[#243B68]">
                {selectedProject.name}
              </span>
              <span className="text-slate-300">·</span>
              <span className="truncate text-xs font-medium text-[#51698F]">
                Client:{" "}
                {selectedProject.client?.name ??
                  t("createTicket.noClientLinked")}
              </span>
            </div>
          ) : null}
        </div>
      </div>
      {loadingEdit ? (
        <div className="rounded-md border bg-card px-4 py-3 text-sm text-muted-foreground">
          {t("createTicket.loadingTicketData")}
        </div>
      ) : null}
      <fieldset disabled={isSubmitting} className="space-y-6">
        <section className="rounded-lg border bg-card p-4">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,380px)] lg:items-start">
            <div className="space-y-4">
              {!selectedProject || !projectId ? (
                <>
                  <div className="space-y-1.5">
                    <div className="flex h-5 items-center justify-between">
                      <div className="flex min-w-0 items-center gap-2">
                        <Label>
                          {t("ticketDetail.project")}{" "}
                          <span className="text-accent">*</span>
                        </Label>
                        <span className="truncate text-xs text-muted-foreground">
                          {selectedProject
                            ? t("createTicket.selectedClient", {
                                clientName:
                                  selectedProject.client?.name ??
                                  t("createTicket.noClientLinked"),
                              })
                            : ""}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="text-xs text-accent hover:underline"
                        onClick={openAddProjectModal}
                      >
                        {t("createTicket.addProject")}
                      </button>
                    </div>
                    <select
                      className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                      value={projectId}
                      onChange={(e) => setProjectId(e.target.value)}
                      disabled={lookupLoading}
                    >
                      <option value="">
                        {t("createTicket.selectProject")}
                      </option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex h-5 items-center">
                      <Label>{t("createTicket.ticketType")}</Label>
                    </div>
                    {projectId ? (
                      <select
                        className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                        value={ticketType}
                        onChange={(e) => {
                          setTitleDirty(false);
                          setTicketType(e.target.value as TicketType);
                        }}
                      >
                        {orderedTicketTypeTabs.map((item) => (
                          <option key={item.key} value={item.key}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-2.5 text-sm text-slate-600">
                        Select a project first to choose the ticket type.
                      </div>
                    )}
                  </div>
                </>
              ) : null}
              <div className="space-y-1.5">
                <div className="flex h-5 items-center">
                  <Label>
                    {t("createTicket.title")}{" "}
                    <span className="text-accent">*</span>
                  </Label>
                </div>
                <Input
                  value={title}
                  placeholder={titlePlaceholder}
                  onChange={(e) => {
                    setTitleDirty(true);
                    setTitle(e.target.value);
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label>{descriptionLabel}</Label>
                  {ticketType === "tm" ? (
                    <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                      <Checkbox
                        checked={showDescriptionToCustomer}
                        onCheckedChange={(checked) =>
                          setShowDescriptionToCustomer(Boolean(checked))
                        }
                      />
                      {t("createTicket.showToCustomer")}
                    </label>
                  ) : null}
                </div>
                <Textarea
                  value={description}
                  placeholder={effectiveDescriptionPlaceholder}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                />
              </div>
              {ticketType === "co" ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Reason for change</Label>
                    <select
                      className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                      value={changeOrderReason}
                      onChange={(e) =>
                        setChangeOrderReason(
                          e.target.value as ChangeOrderReason,
                        )
                      }
                    >
                      {CHANGE_ORDER_REASON_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Change type</Label>
                    <div className="flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
                      {CHANGE_ORDER_TYPE_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setChangeOrderType(option.value)}
                          className={`flex-1 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                            changeOrderType === option.value
                              ? "border border-accent/40 bg-accent/15 text-[#0C1F4F] shadow-sm"
                              : "text-[#51698F] hover:bg-white"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
              {ticketType === "ew" ? (
                <div className="space-y-1.5">
                  <Label>Extra work reason</Label>
                  <select
                    className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                    value={extraWorkReason}
                    onChange={(e) =>
                      setExtraWorkReason(e.target.value as ExtraWorkReason)
                    }
                  >
                    {EXTRA_WORK_REASON_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>

            {selectedProject ? (
              <Collapsible
                open={projectContextOpen}
                onOpenChange={setProjectContextOpen}
                className="lg:mt-5"
              >
                <div
                  className={`rounded-lg border bg-slate-50/60 transition-colors duration-200 ${
                    projectContextOpen
                      ? "border-[#0C1F4F]/25 bg-white shadow-[0_8px_24px_rgba(15,23,42,0.05)]"
                      : "border-slate-200"
                  }`}
                >
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0C1F4F]/20"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#0C1F4F]">
                          Project ticket context
                        </p>
                        <p className="text-xs font-medium text-[#51698F]">
                          {projectContextSummaryLine}
                        </p>
                      </div>
                      <ChevronDown
                        className={`h-4 w-4 shrink-0 text-[#51698F] transition-transform duration-200 ${
                          projectContextOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="ticket-context-accordion-content">
                    <div className="px-2.5 pb-2.5">
                      <ProjectContextSummary
                        compact
                        project={selectedProject}
                        context={selectedProjectContextQuery.data}
                      />
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ) : null}
          </div>

        </section>

        <section className="space-y-4 rounded-lg border bg-card p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="font-semibold">Price & Estimate</h2>
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                Enter a final price, or use the calculator to build a detailed
                estimate first.
              </p>
            </div>
            {!internalEstimateSaved ? (
              <Button
                type="button"
                onClick={openInternalEstimateCalculator}
                className="bg-accent text-accent-foreground hover:bg-accent/90"
              >
                Open estimate calculator
              </Button>
            ) : null}
          </div>

          {internalEstimateSaved ? (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-accent/30 bg-accent/5 px-4 py-3 shadow-[0_6px_18px_rgba(243,140,22,0.08)]">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#0C1F4F]">
                  Internal estimate saved
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Internal subtotal{" "}
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(internalSubtotal)}
                  {" - "}Customer price{" "}
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(Number(customerPrice || 0))}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label="View internal estimate"
                  title="View internal estimate"
                  onClick={() => setEstimatePreviewOpen(true)}
                  className="h-8 w-8 text-[#0C1F4F] hover:bg-white/80"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label="Edit internal estimate"
                  title="Edit internal estimate"
                  onClick={openInternalEstimateCalculator}
                  className="h-8 w-8 text-[#0C1F4F] hover:bg-white/80"
                >
                  <Pen className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label="Remove internal estimate"
                  title="Remove internal estimate"
                  className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => {
                    setInternalEstimateSaved(false);
                    setCustomerDisplayMode("final_price_only");
                    setPricingMode("simple_total");
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : null}

          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Customer price</Label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm font-medium text-muted-foreground">
                  $
                </span>
                <Input
                  value={customerPrice}
                  onChange={(e) => handleCustomerPriceChange(e.target.value)}
                  placeholder="0.00"
                  type="number"
                  min={0}
                  className="pl-7"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Discount</Label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm font-medium text-muted-foreground">
                  $
                </span>
                <Input
                  value={discountAmount}
                  onChange={(e) => setDiscountAmount(e.target.value)}
                  placeholder="0.00"
                  type="number"
                  min={0}
                  className="pl-7"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Tax amount</Label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm font-medium text-muted-foreground">
                  $
                </span>
                <Input
                  value={taxAmount}
                  onChange={(e) => {
                    setTaxManuallyOverridden(true);
                    setTaxAmount(e.target.value);
                  }}
                  placeholder="0.00"
                  type="number"
                  min={0}
                  className="pl-7"
                />
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-slate-50 px-4 py-3 text-sm">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span className="font-medium">
                ${Number(customerPrice || 0).toFixed(2)}
              </span>
            </div>
            <div className="mt-1 flex justify-between">
              <span>Discount</span>
              <span className="font-medium">
                -${Number(discountAmount || 0).toFixed(2)}
              </span>
            </div>
            <div className="mt-1 flex justify-between">
              <span>Tax</span>
              <span className="font-medium">
                ${Number(taxAmount || 0).toFixed(2)}
              </span>
            </div>
            <div className="mt-2 flex justify-between border-t pt-2">
              <span className="font-semibold">Total</span>
              <span className="font-bold">${total.toFixed(2)}</span>
            </div>
          </div>

          {internalEstimateSaved ? (
            <div className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white px-3 py-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-sm font-semibold text-[#0C1F4F]">
                  Customer view
                </h3>
                <p className="text-xs text-muted-foreground">
                  Controls the approval link and PDF.
                </p>
              </div>
              <div className="flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
                {[
                  ["final_price_only", "Final price only"],
                  ["summary_breakdown", "Summary breakdown"],
                  ["full_itemized_breakdown", "Full itemized"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() =>
                      setCustomerDisplayMode(value as CustomerDisplayMode)
                    }
                    className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                      customerDisplayMode === value
                        ? "border border-accent/40 bg-accent/15 text-[#0C1F4F] shadow-sm"
                        : "text-[#51698F] hover:bg-white"
                    }`}
                  >
                    {customerDisplayMode === value ? (
                      <Check className="mr-1.5 inline h-3.5 w-3.5" />
                    ) : null}
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </section>

        {projectId && ticketType === "co" ? (
          <section className="rounded-lg border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="font-semibold text-[#0C1F4F]">
                Contract Impact
              </h3>
              <span className="text-xs font-medium text-[#51698F]">
                {changeOrderTypeLabel}
              </span>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#51698F]">
                  Current approved
                </p>
                <p className="mt-1 text-base font-semibold text-[#0C1F4F]">
                  {formatCurrency(changeOrderContractSummary.currentContractTotal)}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#51698F]">
                  This change order
                </p>
                <p
                  className={`mt-1 text-base font-semibold ${
                    changeOrderAmount < 0
                      ? "text-destructive"
                      : changeOrderAmount > 0
                        ? "text-accent"
                        : "text-[#0C1F4F]"
                  }`}
                >
                  {changeOrderAmount > 0 ? "+" : ""}
                  {formatCurrency(changeOrderAmount)}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#51698F]">
                  Contract total after this
                </p>
                <p className="mt-1 text-lg font-bold text-accent">
                  {formatCurrency(projectedContractTotal)}
                </p>
              </div>
            </div>
          </section>
        ) : null}

        <section className="hidden rounded-lg border bg-card p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">{t("createTicket.lineItems")}</h2>
            <Button
              size="sm"
              onClick={addLine}
              className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90"
            >
              <Plus className="h-3.5 w-3.5" /> {t("createTicket.add")}
            </Button>
          </div>
          <div className="space-y-3">
            {lineItems.map((item) => {
              const normalizedCategory = normalizeLineItemCategory(
                item.category,
              );
              const lineTotal = calculateLineItemTotal(item);
              return (
                <div
                  key={item.id}
                  className={`rounded-md border p-3 space-y-2 ${
                    ticketType === "co" && item.direction === "DEDUCT"
                      ? "border-amber-300 bg-amber-50/70"
                      : ""
                  }`}
                >
                  {ticketType === "co" ? (
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-white/80 px-3 py-2">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#51698F]">
                          Change direction
                        </p>
                        <p className="text-sm font-medium text-[#0C1F4F]">
                          {item.direction === "DEDUCT"
                            ? "Credit / Deduction"
                            : "Add to contract"}
                        </p>
                      </div>
                      <div className="inline-flex rounded-md border border-accent bg-muted/20 p-1">
                        <button
                          type="button"
                          className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                            item.direction === "ADD"
                              ? "bg-primary text-primary-foreground"
                              : "text-muted-foreground hover:bg-muted"
                          }`}
                          onClick={() =>
                            updateLine(item.id, { direction: "ADD" })
                          }
                        >
                          Add (+)
                        </button>
                        <button
                          type="button"
                          className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                            item.direction === "DEDUCT"
                              ? "bg-primary text-primary-foreground"
                              : "text-muted-foreground hover:bg-muted"
                          }`}
                          onClick={() =>
                            updateLine(item.id, { direction: "DEDUCT" })
                          }
                        >
                          Deduct (-)
                        </button>
                      </div>
                    </div>
                  ) : null}
                  {normalizedCategory === "LABOR" ? (
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-end gap-3">
                        <select
                          className="h-9 shrink-0 rounded-md border bg-background px-2 text-sm"
                          style={{
                            width: 220,
                            minWidth: 220,
                            flex: "0 0 220px",
                          }}
                          value={normalizedCategory}
                          onChange={(e) =>
                            changeLineCategory(
                              item.id,
                              normalizeLineItemCategory(e.target.value),
                            )
                          }
                        >
                          {lineItemOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <div className="flex flex-wrap items-center gap-2.5 min-h-9">
                          <Label className="text-xs text-muted-foreground">
                            {t("createTicket.pricingMethod")}
                          </Label>
                          <div className="inline-flex rounded-md border border-accent bg-muted/20 p-1">
                            <button
                              type="button"
                              className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${item.pricingMethod === "HOURLY" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                              onClick={() =>
                                updateLine(item.id, {
                                  pricingMethod: "HOURLY",
                                  unit: item.unit || "hrs",
                                })
                              }
                            >
                              {t("createTicket.hourly")}
                            </button>
                            <button
                              type="button"
                              className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${item.pricingMethod === "FIXED" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                              onClick={() =>
                                updateLine(item.id, { pricingMethod: "FIXED" })
                              }
                            >
                              {t("createTicket.fixedPrice")}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div
                        className={`grid gap-2 items-end ${item.pricingMethod === "FIXED" ? "sm:grid-cols-[1.5fr_160px_120px_38px]" : "sm:grid-cols-[1.5fr_110px_130px_120px_38px]"}`}
                      >
                        <div className="relative space-y-1">
                          <Label className="text-xs text-muted-foreground">
                            {t("ticketDetail.table.description")}
                          </Label>
                          <div className="flex items-start gap-2">
                            {renderLineItemThumbnail(item)}
                            <Textarea
                              value={item.description}
                              onChange={(e) =>
                                updateLineDescription(item.id, e.target.value)
                              }
                              onInput={(e) =>
                                syncLineDescriptionHeight(
                                  e.currentTarget,
                                  e.currentTarget.value,
                                )
                              }
                              onFocus={() => setActivePriceBookLineId(item.id)}
                              onBlur={() => {
                                window.setTimeout(() => {
                                  setActivePriceBookLineId((prev) =>
                                    prev === item.id ? null : prev,
                                  );
                                }, 120);
                              }}
                              placeholder={descriptionPlaceholderForCategory(
                                normalizedCategory,
                              )}
                              rows={1}
                              className={lineDescriptionClassName(
                                item.description,
                              )}
                              ref={(node) => {
                                if (node)
                                  syncLineDescriptionHeight(
                                    node,
                                    item.description,
                                  );
                              }}
                            />
                          </div>
                          {renderPriceBookAutocomplete(item)}
                        </div>
                        {item.pricingMethod === "HOURLY" ? (
                          <>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">
                                {t("createTicket.hours")}
                              </Label>
                              <div className="relative">
                                <Input
                                  value={item.quantity}
                                  onChange={(e) =>
                                    updateLine(item.id, {
                                      quantity: e.target.value,
                                    })
                                  }
                                  placeholder="0"
                                  type="number"
                                  min={0}
                                  className="pr-10"
                                />
                                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">
                                  hrs
                                </span>
                              </div>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">
                                {t("createTicket.ratePerHour")}
                              </Label>
                              <Input
                                value={item.unitCost}
                                onChange={(e) =>
                                  updateLine(item.id, {
                                    unitCost: e.target.value,
                                  })
                                }
                                placeholder="0"
                                type="number"
                                min={0}
                              />
                            </div>
                          </>
                        ) : (
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">
                              {t("createTicket.amountWithCurrency")}
                            </Label>
                            <Input
                              value={item.fixedAmount}
                              onChange={(e) =>
                                updateLine(item.id, {
                                  fixedAmount: e.target.value,
                                })
                              }
                              placeholder="0"
                              type="number"
                              min={0}
                            />
                          </div>
                        )}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">
                            {t("ticketDetail.table.total")}
                          </Label>
                          <Input
                            value={lineTotal.toFixed(2)}
                            readOnly
                            className="bg-muted/20"
                          />
                        </div>
                        <button
                          type="button"
                          className="inline-flex h-9 w-9 items-center justify-center self-end rounded hover:bg-destructive/10"
                          onClick={() => removeLine(item.id)}
                        >
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </div>
                    </div>
                  ) : null}
                  {normalizedCategory === "OTHER" ? (
                    <select
                      className="h-9 shrink-0 rounded-md border bg-background px-2 text-sm"
                      style={{ width: 220, minWidth: 220, flex: "0 0 220px" }}
                      value={normalizedCategory}
                      onChange={(e) =>
                        changeLineCategory(
                          item.id,
                          normalizeLineItemCategory(e.target.value),
                        )
                      }
                    >
                      {lineItemOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : null}
                  {normalizedCategory === "MATERIAL" ? (
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-end gap-3">
                        <select
                          className="h-9 shrink-0 rounded-md border bg-background px-2 text-sm"
                          style={{
                            width: 220,
                            minWidth: 220,
                            flex: "0 0 220px",
                          }}
                          value={normalizedCategory}
                          onChange={(e) =>
                            changeLineCategory(
                              item.id,
                              normalizeLineItemCategory(e.target.value),
                            )
                          }
                        >
                          {lineItemOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <div className="flex flex-wrap items-center gap-2.5 min-h-9">
                          <div className="inline-flex rounded-md border border-accent bg-muted/20 p-1">
                            <button
                              type="button"
                              className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${item.useUnit ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                              onClick={() =>
                                updateLine(item.id, { useUnit: true })
                              }
                            >
                              {t("createTicket.includeUnit")}
                            </button>
                            <button
                              type="button"
                              className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${!item.useUnit ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                              onClick={() =>
                                updateLine(item.id, { useUnit: false })
                              }
                            >
                              {t("createTicket.hideUnit")}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div
                        className={`grid gap-2 items-end ${item.useUnit ? "sm:grid-cols-[1.5fr_110px_100px_130px_120px_38px]" : "sm:grid-cols-[1.5fr_110px_130px_120px_38px]"}`}
                      >
                        <div className="relative space-y-1">
                          <Label className="text-xs text-muted-foreground">
                            {t("createTicket.materialDescription")}
                          </Label>
                          <div className="flex items-start gap-2">
                            {renderLineItemThumbnail(item)}
                            <Textarea
                              value={item.description}
                              onChange={(e) =>
                                  updateLineDescription(item.id, e.target.value)
                              }
                              onInput={(e) =>
                                syncLineDescriptionHeight(
                                  e.currentTarget,
                                  e.currentTarget.value,
                                )
                              }
                              onFocus={() => setActivePriceBookLineId(item.id)}
                              onBlur={() => {
                                window.setTimeout(() => {
                                  setActivePriceBookLineId((prev) =>
                                    prev === item.id ? null : prev,
                                  );
                                }, 120);
                              }}
                              placeholder={descriptionPlaceholderForCategory(
                                normalizedCategory,
                              )}
                              rows={1}
                              className={lineDescriptionClassName(
                                item.description,
                              )}
                              ref={(node) => {
                                if (node)
                                  syncLineDescriptionHeight(
                                    node,
                                    item.description,
                                  );
                              }}
                            />
                          </div>
                          {renderPriceBookAutocomplete(item)}
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">
                            {t("createTicket.quantity")}
                          </Label>
                          <Input
                            value={item.quantity}
                            onChange={(e) =>
                              updateLine(item.id, { quantity: e.target.value })
                            }
                            placeholder="0"
                            type="number"
                            min={0}
                          />
                        </div>
                        {item.useUnit ? (
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">
                              {t("ticketDetail.table.unit")}
                            </Label>
                            <select
                              className="h-9 rounded-md border bg-background px-2 text-sm w-full"
                              value={item.unit}
                              onChange={(e) =>
                                updateLine(item.id, { unit: e.target.value })
                              }
                            >
                              {materialUnitOptions.map((unit) => (
                                <option key={unit} value={unit}>
                                  {unit}
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : null}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">
                            {t("createTicket.unitPrice")}
                          </Label>
                          <Input
                            value={item.unitCost}
                            onChange={(e) =>
                              updateLine(item.id, { unitCost: e.target.value })
                            }
                            placeholder="0"
                            type="number"
                            min={0}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">
                            {t("ticketDetail.table.total")}
                          </Label>
                          <Input
                            value={lineTotal.toFixed(2)}
                            readOnly
                            className="bg-muted/20"
                          />
                        </div>
                        <button
                          type="button"
                          className="inline-flex h-9 w-9 items-center justify-center self-end rounded hover:bg-destructive/10"
                          onClick={() => removeLine(item.id)}
                        >
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </div>
                    </div>
                  ) : null}
                  {normalizedCategory === "EQUIPMENT" ? (
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-end gap-3">
                        <select
                          className="h-9 shrink-0 rounded-md border bg-background px-2 text-sm"
                          style={{
                            width: 220,
                            minWidth: 220,
                            flex: "0 0 220px",
                          }}
                          value={normalizedCategory}
                          onChange={(e) =>
                            changeLineCategory(
                              item.id,
                              normalizeLineItemCategory(e.target.value),
                            )
                          }
                        >
                          {lineItemOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <div className="flex flex-wrap items-center gap-2.5 min-h-9">
                          <Label className="text-xs text-muted-foreground">
                            {t("createTicket.pricingMethod")}
                          </Label>
                          <div className="inline-flex rounded-md border border-accent bg-muted/20 p-1">
                            <button
                              type="button"
                              className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${item.pricingMethod === "HOURLY" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                              onClick={() =>
                                updateLine(item.id, {
                                  pricingMethod: "HOURLY",
                                  unit: ["hrs", "days", "weeks"].includes(
                                    item.unit,
                                  )
                                    ? item.unit
                                    : "hrs",
                                })
                              }
                            >
                              Time-based
                            </button>
                            <button
                              type="button"
                              className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${item.pricingMethod === "FIXED" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                              onClick={() =>
                                updateLine(item.id, {
                                  pricingMethod: "FIXED",
                                  quantity: "1",
                                  unit: "ea",
                                })
                              }
                            >
                              {t("createTicket.fixedPrice")}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div
                        className={`grid gap-2 items-end ${item.pricingMethod === "FIXED" ? "sm:grid-cols-[1.5fr_160px_120px_38px]" : "sm:grid-cols-[1.5fr_110px_120px_130px_120px_38px]"}`}
                      >
                        <div className="relative space-y-1">
                          <Label className="text-xs text-muted-foreground">
                            {t("createTicket.equipmentDescription")}
                          </Label>
                          <div className="flex items-start gap-2">
                            {renderLineItemThumbnail(item)}
                            <Textarea
                              value={item.description}
                              onChange={(e) =>
                                  updateLineDescription(item.id, e.target.value)
                              }
                              onInput={(e) =>
                                syncLineDescriptionHeight(
                                  e.currentTarget,
                                  e.currentTarget.value,
                                )
                              }
                              onFocus={() => setActivePriceBookLineId(item.id)}
                              onBlur={() => {
                                window.setTimeout(() => {
                                  setActivePriceBookLineId((prev) =>
                                    prev === item.id ? null : prev,
                                  );
                                }, 120);
                              }}
                              placeholder={descriptionPlaceholderForCategory(
                                normalizedCategory,
                              )}
                              rows={1}
                              className={lineDescriptionClassName(
                                item.description,
                              )}
                              ref={(node) => {
                                if (node)
                                  syncLineDescriptionHeight(
                                    node,
                                    item.description,
                                  );
                              }}
                            />
                          </div>
                          {renderPriceBookAutocomplete(item)}
                        </div>
                        {item.pricingMethod === "FIXED" ? (
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">
                              {t("createTicket.amountWithCurrency")}
                            </Label>
                            <Input
                              value={item.fixedAmount}
                              onChange={(e) =>
                                updateLine(item.id, {
                                  fixedAmount: e.target.value,
                                })
                              }
                              placeholder="0"
                              type="number"
                              min={0}
                            />
                          </div>
                        ) : (
                          <>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">
                                {t("createTicket.duration")}
                              </Label>
                              <Input
                                value={item.quantity}
                                onChange={(e) =>
                                  updateLine(item.id, {
                                    quantity: e.target.value,
                                  })
                                }
                                placeholder="0"
                                type="number"
                                min={0}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">
                                Time unit
                              </Label>
                              <select
                                className="h-9 rounded-md border bg-background px-2 text-sm w-full"
                                value={
                                  ["hrs", "days", "weeks"].includes(item.unit)
                                    ? item.unit
                                    : "hrs"
                                }
                                onChange={(e) =>
                                  updateLine(item.id, { unit: e.target.value })
                                }
                              >
                                <option value="hrs">Hours</option>
                                <option value="days">Days</option>
                                <option value="weeks">Weeks</option>
                              </select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">
                                {t("createTicket.rate")}
                              </Label>
                              <Input
                                value={item.unitCost}
                                onChange={(e) =>
                                  updateLine(item.id, {
                                    unitCost: e.target.value,
                                  })
                                }
                                placeholder="0"
                                type="number"
                                min={0}
                              />
                            </div>
                          </>
                        )}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">
                            {t("ticketDetail.table.total")}
                          </Label>
                          <Input
                            value={lineTotal.toFixed(2)}
                            readOnly
                            className="bg-muted/20"
                          />
                        </div>
                        <button
                          type="button"
                          className="inline-flex h-9 w-9 items-center justify-center self-end rounded hover:bg-destructive/10"
                          onClick={() => removeLine(item.id)}
                        >
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </div>
                    </div>
                  ) : null}
                  {normalizedCategory === "OTHER" ? (
                    <div className="grid sm:grid-cols-[1.5fr_160px_120px_38px] gap-2 items-end">
                      <div className="relative space-y-1">
                        <Label className="text-xs text-muted-foreground">
                          {t("ticketDetail.table.description")}
                        </Label>
                        <div className="flex items-start gap-2">
                          {renderLineItemThumbnail(item)}
                          <Textarea
                            value={item.description}
                            onChange={(e) =>
                                updateLineDescription(item.id, e.target.value)
                            }
                            onInput={(e) =>
                              syncLineDescriptionHeight(
                                e.currentTarget,
                                e.currentTarget.value,
                              )
                            }
                            onFocus={() => setActivePriceBookLineId(item.id)}
                            onBlur={() => {
                              window.setTimeout(() => {
                                setActivePriceBookLineId((prev) =>
                                  prev === item.id ? null : prev,
                                );
                              }, 120);
                            }}
                            placeholder={descriptionPlaceholderForCategory(
                              normalizedCategory,
                            )}
                            rows={1}
                            className={lineDescriptionClassName(
                              item.description,
                            )}
                            ref={(node) => {
                              if (node)
                                syncLineDescriptionHeight(
                                  node,
                                  item.description,
                                );
                            }}
                          />
                        </div>
                        {renderPriceBookAutocomplete(item)}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">
                          {t("billingExport.amount")}
                        </Label>
                        <Input
                          value={item.unitCost}
                          onChange={(e) =>
                            updateLine(item.id, {
                              unitCost: e.target.value,
                              quantity: "1",
                              unit: "ea",
                            })
                          }
                          placeholder="0"
                          type="number"
                          min={0}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">
                          {t("ticketDetail.table.total")}
                        </Label>
                        <Input
                          value={Number(item.unitCost || 0).toFixed(2)}
                          readOnly
                          className="bg-muted/20"
                        />
                      </div>
                      <button
                        type="button"
                        className="inline-flex h-9 w-9 items-center justify-center self-end rounded hover:bg-destructive/10"
                        onClick={() => removeLine(item.id)}
                      >
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          <div className="grid sm:grid-cols-3 gap-3 items-end">
            <div className="sm:col-span-2" />
            <div className="space-y-1.5">
              <Label>{t("createTicket.taxAmount")}</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={taxAmount}
                onChange={(e) => {
                  const next = e.target.value;
                  setTaxAmount(next);
                  setTaxManuallyOverridden(next.trim().length > 0);
                }}
                onBlur={() => {
                  if (taxAmount.trim().length === 0) {
                    setTaxManuallyOverridden(false);
                    return;
                  }
                  const parsed = Number(taxAmount);
                  if (Number.isFinite(parsed)) {
                    setTaxAmount(parsed.toFixed(2));
                  }
                }}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">
                {t("createTicket.defaultRate", { rate: defaultTaxRate })}{" "}
                {taxManuallyOverridden
                  ? t("createTicket.manualOverrideActive")
                  : t("createTicket.autoApplied")}
              </p>
            </div>
            <div className="sm:col-span-3 text-sm text-right">
              {t("ticketDetail.subtotal")}:{" "}
              <span className="font-medium">${subtotal.toFixed(2)}</span> |{" "}
              {t("ticketDetail.tax")}:{" "}
              <span className="font-medium">
                ${Number(taxAmount || 0).toFixed(2)}
              </span>{" "}
              | {t("ticketDetail.table.total")}:{" "}
              <span className="font-bold">${total.toFixed(2)}</span>
            </div>
          </div>
        </section>

        <section className="rounded-lg border bg-card p-5 space-y-4">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="font-semibold">
              {t("createTicket.evidenceAttachments")}
            </h2>
            <div className="flex items-center justify-between gap-6 sm:min-w-[540px]">
              <p className="text-xs text-muted-foreground">
                {t("createTicket.allowedAttachments")}
              </p>
              <label className="inline-flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                <Checkbox
                  checked={showAttachmentsToCustomer}
                  onCheckedChange={(checked) =>
                    setShowAttachmentsToCustomer(Boolean(checked))
                  }
                />
                {t("createTicket.showToCustomer")}
              </label>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => {
                if (starterAttachmentLimitReached) {
                  toast({
                    title: t("createTicket.toasts.starterAttachmentLimitTitle"),
                    description: t(
                      "createTicket.toasts.starterAttachmentLimitDescription",
                    ),
                    variant: "destructive",
                  });
                  return;
                }
                fileInputRef.current?.click();
              }}
            >
              <Paperclip className="h-4 w-4" /> {t("createTicket.uploadFiles")}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => void openCamera()}
            >
              <Camera className="h-4 w-4" /> {t("createTicket.takePhoto")}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple={user?.currentPlan !== "STARTER"}
              className="hidden"
              accept=".jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,.pdf"
              onChange={(e) => {
                void addAttachmentFiles(Array.from(e.target.files ?? []));
                e.currentTarget.value = "";
              }}
            />
          </div>
          {user?.currentPlan === "STARTER" ? (
            <p className="text-xs text-amber-700">
              {t("createTicket.starterAttachmentLimitHint")}
            </p>
          ) : null}
          {attachments.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("createTicket.noAttachmentsYet")}
            </p>
          ) : (
            <div className="space-y-2">
              {attachments.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center justify-between rounded-md border px-3 py-2 gap-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-12 w-12 rounded border bg-muted/20 flex items-center justify-center overflow-hidden shrink-0">
                      {file.previewUrl ? (
                        <img
                          src={file.previewUrl}
                          alt={file.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-[10px] text-muted-foreground font-medium">
                          PDF
                        </span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024).toFixed(1)} KB •{" "}
                        {file.mimeType.includes("pdf")
                          ? "PDF"
                          : t("createTicket.image")}
                      </p>
                      {file.status === "uploading" ? (
                        <div className="mt-1 w-44 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full bg-accent transition-all"
                            style={{ width: `${file.progress}%` }}
                          />
                        </div>
                      ) : null}
                      {file.status === "failed" && file.error ? (
                        <p className="text-xs text-destructive mt-1">
                          {file.error}
                        </p>
                      ) : null}
                      {file.status === "uploaded" ? (
                        <p className="text-xs text-success mt-1">
                          {t("createTicket.uploaded")}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {file.status === "failed" ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void retryAttachment(file.id)}
                        disabled={processingUploads}
                      >
                        Retry
                      </Button>
                    ) : null}
                    <button
                      type="button"
                      className="p-1 rounded hover:bg-destructive/10 disabled:opacity-50"
                      onClick={() => void removeAttachment(file.id)}
                      disabled={file.status === "uploading"}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {processingUploads ? (
            <p className="text-xs text-muted-foreground">
              {t("createTicket.uploadingAttachments")}
            </p>
          ) : null}
        </section>

        <section className="rounded-lg border bg-card p-5 space-y-4">
          <div className="space-y-2">
            <h2 className="font-semibold">
              {t("createTicket.approvalMethod")}
            </h2>
            <div className="grid gap-1 rounded-md border border-accent p-1 sm:grid-cols-2">
              <button
                type="button"
                className={`rounded-md py-2 px-3 text-sm font-medium text-left transition-colors ${approvalMethod === "remote" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                onClick={() => setApprovalMethod("remote")}
              >
                {t("createTicket.sendApprovalLink")}
              </button>
              <button
                type="button"
                className={`rounded-md py-2 px-3 text-sm font-medium text-left transition-colors ${approvalMethod === "onsite" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                onClick={() => setApprovalMethod("onsite")}
              >
                {t("createTicket.captureSignatureOnSite")}
              </button>
            </div>
          </div>
          <div className="min-h-[34rem] sm:min-h-[24rem]">
            {approvalMethod === "remote" && (
              <div className="grid sm:grid-cols-2 gap-4 animate-fade-in">
                <div className="space-y-1.5">
                  <Label>{t("createTicket.deliveryMethod")}</Label>
                  {canUseSmsDelivery ? (
                    <select
                      value={deliveryMethod}
                      onChange={(e) =>
                        setDeliveryMethod(
                          e.target.value as ApprovalDeliveryMethod,
                        )
                      }
                      className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                    >
                      <option value="EMAIL">
                        {t("createTicket.deliveryEmail")}
                      </option>
                      <option value="SMS">
                        {t("createTicket.deliverySms")}
                      </option>
                      <option value="EMAIL_AND_SMS">
                        {t("createTicket.deliveryEmailAndSms")}
                      </option>
                    </select>
                  ) : (
                    <div className="h-9 w-full rounded-md border bg-muted/30 px-3 text-sm flex items-center text-foreground">
                      {t("createTicket.deliveryEmail")}
                    </div>
                  )}
                  {!canUseSmsDelivery ? (
                    <p className="text-xs text-muted-foreground">
                      {smsAvailabilityReason}
                    </p>
                  ) : null}
                </div>
                {workspaceAutoRemindersEnabled ? (
                  <div className="space-y-1.5">
                    <Label>{t("ticketDetail.autoReminder")}</Label>
                    <select
                      className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                      value={reminderCadenceOption}
                      onChange={(e) => setReminderCadenceOption(e.target.value)}
                    >
                      {COMMON_CADENCE_OPTIONS.map((d) => (
                        <option key={d} value={String(d)}>
                          {d === 3
                            ? t("ticketDetail.recommendedDayCount", {
                                count: d,
                              })
                            : t("ticketDetail.dayCount", { count: d })}
                        </option>
                      ))}
                      <option value="custom">{t("ticketDetail.custom")}</option>
                    </select>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label>{t("ticketDetail.autoReminder")}</Label>
                    <div className="h-9 w-full rounded-md border bg-muted/30 px-3 text-sm flex items-center text-muted-foreground">
                      {t("createTicket.autoRemindersOff")}
                    </div>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>
                    {t("createTicket.recipientName")}{" "}
                    <span className="text-accent">*</span>
                  </Label>
                  <Input
                    value={recipientName}
                    onChange={(e) => {
                      setRecipientNameTouched(true);
                      setRecipientName(e.target.value);
                      if (recipientNameError) setRecipientNameError("");
                    }}
                    className={
                      recipientNameError
                        ? "border-destructive focus-visible:ring-destructive/40"
                        : ""
                    }
                  />
                  {recipientNameError ? (
                    <p className="text-xs text-destructive">
                      {recipientNameError}
                    </p>
                  ) : null}
                </div>
                {deliveryMethod === "EMAIL" ||
                deliveryMethod === "EMAIL_AND_SMS" ? (
                  <div className="space-y-1.5">
                    <Label>
                      {t("createTicket.recipientEmail")}{" "}
                      <span className="text-accent">*</span>
                    </Label>
                    <Input
                      value={recipientEmail}
                      onChange={(e) => {
                        setRecipientEmailTouched(true);
                        setRecipientEmail(e.target.value);
                        if (recipientEmailError) setRecipientEmailError("");
                      }}
                      type="email"
                      placeholder={
                        hasClientPrimaryEmail
                          ? selectedClientPrimaryEmail
                          : t("createTicket.approverEmailPlaceholder")
                      }
                      className={
                        recipientEmailError
                          ? "border-destructive focus-visible:ring-destructive/40"
                          : ""
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      {hasClientPrimaryEmail
                        ? t("createTicket.clientPrimaryEmailPrefilled")
                        : t("createTicket.enterRecipientEmail")}
                    </p>
                    {recipientEmailError ? (
                      <p className="text-xs text-destructive">
                        {recipientEmailError}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {deliveryMethod === "SMS" ||
                deliveryMethod === "EMAIL_AND_SMS" ? (
                  <div className="space-y-1.5">
                    <Label>
                      {t("createTicket.recipientPhone")}{" "}
                      <span className="text-accent">*</span>
                    </Label>
                    <Input
                      value={recipientPhone}
                      onChange={(e) => {
                        setRecipientPhoneTouched(true);
                        setRecipientPhone(e.target.value);
                        if (recipientPhoneError) setRecipientPhoneError("");
                      }}
                      type="tel"
                      placeholder={
                        normalizedSelectedClientPhone ||
                        t("createTicket.approverPhonePlaceholder")
                      }
                      className={
                        recipientPhoneError
                          ? "border-destructive focus-visible:ring-destructive/40"
                          : ""
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      {normalizedSelectedClientPhone
                        ? t("createTicket.clientPrimaryPhonePrefilled")
                        : t("createTicket.enterRecipientPhone")}
                    </p>
                    {recipientPhoneError ? (
                      <p className="text-xs text-destructive">
                        {recipientPhoneError}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {deliveryUsesSms ? (
                  <SmsConsentConfirmation
                    checked={smsConsentConfirmed}
                    onCheckedChange={(checked) => {
                      setSmsConsentConfirmed(checked);
                      if (checked) setSmsConsentError("");
                    }}
                    error={smsConsentError}
                    className="sm:col-span-2"
                  />
                ) : null}
                {(deliveryMethod === "EMAIL" ||
                  deliveryMethod === "EMAIL_AND_SMS") &&
                !hasClientPrimaryEmail &&
                selectedProject?.client?.id ? (
                  <div className="space-y-1.5 sm:col-span-2">
                    <label className="flex items-start gap-2 text-sm">
                      <Checkbox
                        checked={saveRecipientAsClientPrimary}
                        onCheckedChange={(checked) =>
                          setSaveRecipientAsClientPrimary(Boolean(checked))
                        }
                        className="mt-0.5"
                      />
                      <span>{t("createTicket.saveAsClientPrimaryEmail")}</span>
                    </label>
                  </div>
                ) : null}
                {workspaceAutoRemindersEnabled &&
                reminderCadenceOption === "custom" ? (
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label>{t("ticketDetail.customCadenceDays")}</Label>
                    <Input
                      type="number"
                      min={1}
                      value={customReminderCadenceDays}
                      onChange={(e) =>
                        setCustomReminderCadenceDays(e.target.value)
                      }
                      placeholder={t("ticketDetail.customCadencePlaceholder")}
                    />
                  </div>
                ) : null}
                {deliveryMethod === "EMAIL" ||
                deliveryMethod === "EMAIL_AND_SMS" ? (
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label>
                      {t("createTicket.addNoteIncludedInEmail")}
                    </Label>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t("createTicket.noteIncludedInEmailHelp")}
                    </p>
                  </div>
                ) : null}
              </div>
            )}
            {approvalMethod === "onsite" ? (
              <div className="space-y-4 animate-fade-in">
                <div className="rounded-md border bg-muted/20 p-3 text-sm text-muted-foreground">
                  {t("createTicket.onSiteModeHelp")}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="gap-1.5"
                    disabled={
                      preparingOnsitePreview || finalizingOnsiteApproval
                    }
                    onClick={() => void openOnsitePdfPreview()}
                  >
                    {preparingOnsitePreview ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                    {preparingOnsitePreview
                      ? t("createTicket.preparingPdf")
                      : t("createTicket.reviewPdfBeforeSignature")}
                  </Button>
                </div>
                {capturedSignature ? (
                  <div className="rounded-md border p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="h-20 w-56 border rounded bg-muted/20 flex items-center justify-center overflow-hidden mb-2">
                          <img
                            src={capturedSignature.imageDataUrl}
                            alt={t("createTicket.capturedSignatureAlt")}
                            className="max-h-full max-w-full object-contain"
                          />
                        </div>
                        <p className="font-serif text-xl leading-none">
                          {capturedSignature.signerName}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {capturedSignature.signerTitle ||
                            t("createTicket.noTitle")}{" "}
                          •{" "}
                          {capturedSignature.signerCompany ||
                            t("createTicket.noCompany")}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t("createTicket.capturedAt")}{" "}
                          {new Date(
                            capturedSignature.capturedAt,
                          ).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSignatureRequestedFromPreview(false);
                            openSignatureModal();
                          }}
                        >
                          {t("createTicket.replace")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCapturedSignature(null)}
                        >
                          {t("common.clear")}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>
      </fieldset>

      <div className="sticky bottom-0 z-20 rounded-lg border bg-card/95 backdrop-blur p-3 flex items-center justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => {
            clearCurrentDraft();
            navigate(-1);
          }}
          disabled={isSubmitting}
        >
          {t("createAccount.cancel")}
        </Button>
        <Button
          variant="outline"
          onClick={() => void saveToDraftOnly()}
          disabled={isSubmitting}
        >
          {t("createTicket.saveToDraft")}
        </Button>
        <LoadingButton
          isLoading={
            approvalMethod === "remote"
              ? savingSend || processingUploads
              : savingDraft || processingUploads || finalizingOnsiteApproval
          }
          onClick={handlePrimaryAction}
          disabled={isSubmitting || approvalMethod === "onsite"}
          className="bg-accent text-accent-foreground hover:bg-accent/90 gap-1.5"
        >
          {approvalMethod === "remote" ? (
            <Send className="h-4 w-4" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {approvalMethod === "remote"
            ? deliveryMethod === "EMAIL_AND_SMS"
              ? t("createTicket.saveAndSendEmailAndSms")
              : deliveryMethod === "SMS"
                ? t("createTicket.saveAndSendSms")
                : t("createTicket.saveAndSend")
            : t("createTicket.usePdfReviewToSign")}
        </LoadingButton>
      </div>

      <Dialog
        open={estimateCalculatorOpen}
        onOpenChange={setEstimateCalculatorOpen}
      >
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>Internal Estimate Calculator</DialogTitle>
            <DialogDescription>
              Use this private worksheet to calculate your price. You can choose
              later how much of this breakdown the customer sees.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] space-y-3 overflow-y-auto pr-1">
            {lineItems.map((item, index) => {
              const normalizedCategory = normalizeLineItemCategory(
                item.category,
              );
              const isFlatCost = item.pricingMethod === "FIXED";
              const unitOptions = getLineUnitOptions(normalizedCategory);
              const lineTotal = calculateLineItemTotal(item);
              return (
                <div key={item.id} className="rounded-lg border bg-card p-3">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-[#0C1F4F]">
                      Row {index + 1}
                    </p>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <div
                        className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5"
                        role="group"
                        aria-label={`Pricing method for row ${index + 1}`}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setLinePricingMethod(item.id, "HOURLY")
                          }
                          className={`rounded px-2.5 py-1 text-xs font-semibold transition-colors ${
                            !isFlatCost
                              ? "border border-accent/40 bg-orange-50 text-[#9A4F00] shadow-sm"
                              : "text-[#51698F] hover:bg-white hover:text-[#0C1F4F]"
                          }`}
                        >
                          Per unit
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setLinePricingMethod(item.id, "FIXED")
                          }
                          className={`rounded px-2.5 py-1 text-xs font-semibold transition-colors ${
                            isFlatCost
                              ? "border border-accent/40 bg-orange-50 text-[#9A4F00] shadow-sm"
                              : "text-[#51698F] hover:bg-white hover:text-[#0C1F4F]"
                          }`}
                        >
                          Flat cost
                        </button>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        aria-label="Remove row"
                        onClick={() => removeLine(item.id)}
                        className="h-8 w-8 border-destructive/25 bg-destructive/5 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div
                    className={`grid gap-3 ${
                      isFlatCost
                        ? "md:grid-cols-[150px_1.4fr_140px_120px]"
                        : "md:grid-cols-[150px_1.4fr_90px_120px_120px_110px]"
                    }`}
                  >
                    <div className="space-y-1.5">
                      <Label>Category</Label>
                      <select
                        className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                        value={normalizedCategory}
                        onChange={(e) =>
                          changeLineCategory(
                            item.id,
                            normalizeLineItemCategory(e.target.value),
                          )
                        }
                      >
                        {lineItemOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="relative space-y-1.5">
                      <Label>Description</Label>
                      <Input
                        value={item.description}
                        onChange={(e) =>
                          updateLineDescription(item.id, e.target.value)
                        }
                        onFocus={() => setActivePriceBookLineId(item.id)}
                        onBlur={() => {
                          window.setTimeout(() => {
                            setActivePriceBookLineId((prev) =>
                              prev === item.id ? null : prev,
                            );
                          }, 120);
                        }}
                        placeholder={descriptionPlaceholderForCategory(
                          normalizedCategory,
                        )}
                      />
                      {item.thumbnailUrl ? (
                        <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                          <div className="h-9 w-9 shrink-0 overflow-hidden rounded border bg-muted/20">
                            <img
                              src={item.thumbnailUrl}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <span className="truncate">Price Book image attached</span>
                        </div>
                      ) : null}
                      {renderPriceBookAutocomplete(item)}
                    </div>
                    {!isFlatCost ? (
                      <>
                        <div className="space-y-1.5">
                          <Label>Qty</Label>
                          <Input
                            value={item.quantity}
                            onChange={(e) =>
                              updateLine(item.id, { quantity: e.target.value })
                            }
                            type="number"
                            min={0}
                            placeholder="0"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Unit</Label>
                          <select
                            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                            value={resolveLineUnit(normalizedCategory, item.unit)}
                            onChange={(e) =>
                              updateLine(item.id, { unit: e.target.value })
                            }
                          >
                            {unitOptions.map((unit) => (
                              <option key={unit} value={unit}>
                                {unit}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    ) : null}
                    <div className="space-y-1.5">
                      <Label>{isFlatCost ? "Flat cost" : "Unit cost / Rate"}</Label>
                      <Input
                        value={isFlatCost ? item.fixedAmount : item.unitCost}
                        onChange={(e) =>
                          updateLine(
                            item.id,
                            isFlatCost
                              ? { fixedAmount: e.target.value }
                              : { unitCost: e.target.value },
                          )
                        }
                        type="number"
                        min={0}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Line total</Label>
                      <Input
                        value={lineTotal.toFixed(2)}
                        readOnly
                        className="bg-muted/20"
                      />
                    </div>
                  </div>
                  <div className="mt-3 grid gap-3 md:grid-cols-[1fr_220px]">
                    <Input
                      value={item.details}
                      onChange={(e) =>
                        updateLine(item.id, { details: e.target.value })
                      }
                      placeholder="Internal note (optional)"
                    />
                    <Button type="button" variant="outline" disabled>
                      Hidden from customer
                    </Button>
                  </div>
                </div>
              );
            })}
            <div className="flex justify-end">
              <Button
                type="button"
                onClick={addLine}
                className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90"
              >
                <Plus className="h-4 w-4" /> Add row
              </Button>
            </div>
          </div>
          <div className="rounded-lg border bg-slate-50 p-4 text-sm">
            <div className="grid gap-4 md:grid-cols-[1fr_260px] md:items-end">
              <div>
                <span className="text-muted-foreground">Estimate total</span>
                <p className="mt-1 text-2xl font-bold text-[#0C1F4F]">
                  ${internalSubtotal.toFixed(2)}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Customer price</Label>
                <Input
                  value={customerPrice}
                  onChange={(e) => handleCustomerPriceChange(e.target.value)}
                  placeholder="0.00"
                  type="number"
                  min={0}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setEstimateCalculatorOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={saveInternalEstimate}
              className="bg-accent text-accent-foreground hover:bg-accent/90"
            >
              Save estimate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={estimatePreviewOpen} onOpenChange={setEstimatePreviewOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Internal Estimate Preview</DialogTitle>
            <DialogDescription>
              Review the saved estimate rows without editing.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.12em] text-[#51698F]">
                <tr>
                  <th className="px-3 py-2">Description</th>
                  <th className="px-3 py-2">Category</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2">Unit</th>
                  <th className="px-3 py-2 text-right">Rate</th>
                  <th className="px-3 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {internalEstimatePreviewRows.length ? (
                  internalEstimatePreviewRows.map((row) => (
                    <Fragment key={row.id}>
                      <tr className="align-top">
                        <td className="px-3 py-2">
                          <p className="font-medium text-[#0C1F4F]">
                            {row.description}
                          </p>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {row.category}
                        </td>
                        <td className="px-3 py-2 text-right text-muted-foreground">
                          {row.quantity}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {row.unit}
                        </td>
                        <td className="px-3 py-2 text-right text-muted-foreground">
                          ${row.unitCost.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-[#0C1F4F]">
                          ${row.total.toFixed(2)}
                        </td>
                      </tr>
                      {row.note ? (
                        <tr className="bg-slate-50/60">
                          <td
                            colSpan={6}
                            className="px-3 pb-2 pt-0 text-xs text-muted-foreground"
                          >
                            <span className="font-semibold text-[#51698F]">
                              Internal note:
                            </span>{" "}
                            {row.note}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-8 text-center text-muted-foreground"
                    >
                      No saved estimate rows to preview.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="grid gap-3 rounded-lg border bg-slate-50 px-4 py-3 text-sm sm:grid-cols-2">
            <div>
              <span className="text-muted-foreground">Estimate total</span>
              <p className="mt-1 text-lg font-bold text-[#0C1F4F]">
                ${internalSubtotal.toFixed(2)}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Customer price</span>
              <p className="mt-1 text-lg font-bold text-[#0C1F4F]">
                ${Number(customerPrice || 0).toFixed(2)}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={downloadInternalEstimateCsv}
              disabled={!internalEstimatePreviewRows.length}
            >
              Download CSV
            </Button>
            <Button type="button" onClick={() => setEstimatePreviewOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={zeroTotalConfirmOpen}
        onOpenChange={setZeroTotalConfirmOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("createTicket.sendWithoutLineItemsTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("createTicket.sendWithoutLineItemsDescription")}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t("createTicket.sendAnywayQuestion")}
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setZeroTotalConfirmOpen(false)}
              disabled={savingSend || processingUploads}
            >
              {t("createTicket.goBack")}
            </Button>
            <LoadingButton
              isLoading={savingSend || processingUploads}
              onClick={async () => {
                setZeroTotalConfirmOpen(false);
                await saveAndSend();
              }}
              className="bg-accent text-accent-foreground hover:bg-accent/90"
            >
              {t("createTicket.sendAnyway")}
            </LoadingButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addProjectOpen} onOpenChange={setAddProjectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[#0C1F4F]">
              {t("createTicket.addProjectDialogTitle")}
            </DialogTitle>
            <DialogDescription className="text-[#0C1F4F]/80">
              {t("createTicket.addProjectDialogDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>
                {t("createTicket.projectName")}{" "}
                <span className="text-accent">*</span>
              </Label>
              <Input
                value={projectForm.name}
                onChange={(e) =>
                  setProjectForm((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder={t("createTicket.projectNamePlaceholder")}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("createTicket.projectCode")}</Label>
                <Input
                  value={projectForm.code}
                  onChange={(e) =>
                    setProjectForm((prev) => ({
                      ...prev,
                      code: e.target.value,
                    }))
                  }
                  placeholder={nextProjectCode()}
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>
                    {t("ticketDetail.client")}{" "}
                    <span className="text-accent">*</span>
                  </Label>
                  <button
                    type="button"
                    className="text-xs text-accent hover:underline"
                    onClick={() => setAddProjectClientOpen(true)}
                  >
                    {t("createTicket.addClient")}
                  </button>
                </div>
                {clients.length === 0 ? (
                  <div className="rounded-md border p-2 text-sm text-muted-foreground">
                    {t("clients.noClientsYet")}
                  </div>
                ) : (
                  <select
                    className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                    value={projectForm.clientId}
                    onChange={(e) =>
                      setProjectForm((prev) => ({
                        ...prev,
                        clientId: e.target.value,
                      }))
                    }
                  >
                    <option value="">{t("createTicket.selectClient")}</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>{t("createTicket.jobSiteAddress")}</Label>
              <Input
                value={projectForm.jobSiteAddress}
                onChange={(e) =>
                  setProjectForm((prev) => ({
                    ...prev,
                    jobSiteAddress: e.target.value,
                  }))
                }
                placeholder={t("createTicket.jobSiteAddressPlaceholder")}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddProjectOpen(false)}>
              {t("createAccount.cancel")}
            </Button>
            <Button
              onClick={submitProject}
              disabled={projectSaving}
              className="bg-accent text-accent-foreground hover:bg-accent/90"
            >
              {projectSaving
                ? t("common.saving")
                : t("createTicket.addProject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={addProjectClientOpen}
        onOpenChange={setAddProjectClientOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[#0C1F4F]">
              {t("createTicket.addClientDialogTitle")}
            </DialogTitle>
            <DialogDescription className="text-[#0C1F4F]/80">
              {t("createTicket.addClientDialogDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>
                {t("clients.clientName")} <span className="text-accent">*</span>
              </Label>
              <Input
                value={newProjectClientName}
                onChange={(e) => setNewProjectClientName(e.target.value)}
                placeholder={t("createTicket.clientNamePlaceholder")}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("createTicket.primaryApproverEmailOptional")}</Label>
              <Input
                value={newProjectClientEmail}
                onChange={(e) => setNewProjectClientEmail(e.target.value)}
                type="email"
                placeholder={t("createTicket.primaryApproverEmailPlaceholder")}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddProjectClientOpen(false)}
            >
              {t("createAccount.cancel")}
            </Button>
            <Button
              onClick={addClientForProject}
              disabled={creatingProjectClient}
              className="bg-accent text-accent-foreground hover:bg-accent/90"
            >
              {creatingProjectClient
                ? t("common.saving")
                : t("createTicket.addClient")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={cameraOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeCamera();
            return;
          }
          setCameraOpen(true);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("createTicket.takePhoto")}</DialogTitle>
            <DialogDescription>
              {t("createTicket.takePhotoDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border bg-black overflow-hidden">
              <video
                ref={cameraVideoRef}
                className="w-full h-[280px] object-cover"
                playsInline
                muted
              />
            </div>
            {cameraError ? (
              <p className="text-xs text-destructive">{cameraError}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeCamera}>
              {t("createAccount.cancel")}
            </Button>
            <Button
              type="button"
              onClick={() => void capturePhotoFromCamera()}
              disabled={capturingPhoto || Boolean(cameraError)}
            >
              {capturingPhoto
                ? t("createTicket.capturing")
                : t("createTicket.capture")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={signatureModalOpen}
        onOpenChange={(open) => {
          setSignatureModalOpen(open);
          if (!open) {
            setSignatureError("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[#0C1F4F]">
              {t("createTicket.captureSignature")}
            </DialogTitle>
            <DialogDescription className="text-[#0C1F4F]/80">
              {t("createTicket.captureSignatureDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>
                {t("createTicket.signerName")}{" "}
                <span className="text-accent">*</span>
              </Label>
              <Input
                value={signatureFormName}
                onChange={(e) => setSignatureFormName(e.target.value)}
                placeholder={t("createTicket.signerNamePlaceholder")}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("createTicket.signerTitle")}</Label>
                <Input
                  value={signatureFormTitle}
                  onChange={(e) => setSignatureFormTitle(e.target.value)}
                  placeholder={t("createTicket.signerTitlePlaceholder")}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("createTicket.signerCompany")}</Label>
                <Input
                  value={signatureFormCompany}
                  onChange={(e) => setSignatureFormCompany(e.target.value)}
                  placeholder={t("createTicket.signerCompanyPlaceholder")}
                />
              </div>
            </div>
            <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
              <div className="space-y-2">
                <div
                  className="rounded-md border bg-background h-44 relative touch-none"
                  style={{ touchAction: "none" }}
                >
                  <canvas
                    ref={canvasRef}
                    className="w-full h-full"
                    onPointerDown={onSignaturePointerDown}
                    onPointerMove={onSignaturePointerMove}
                    onPointerUp={finishSignatureStroke}
                    onPointerCancel={finishSignatureStroke}
                    onPointerLeave={finishSignatureStroke}
                  />
                  {signatureStrokes.length === 0 ? (
                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-sm text-muted-foreground">
                      {t("createTicket.signHere")}
                    </div>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={clearSignaturePad}
                  >
                    {t("common.clear")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={undoSignatureStroke}
                    disabled={signatureStrokes.length === 0}
                  >
                    {t("createTicket.undo")}
                  </Button>
                </div>
              </div>
            </div>
            {signatureError ? (
              <p className="text-sm text-destructive">{signatureError}</p>
            ) : null}
            <p className="text-xs text-muted-foreground">
              {t("createTicket.timestamp")}: {new Date().toLocaleString()}
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSignatureModalOpen(false)}
            >
              {t("createAccount.cancel")}
            </Button>
            <Button
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              onClick={() => {
                if (!signatureFormName.trim()) {
                  toast({
                    title: t("createTicket.toasts.signerNameRequired"),
                    variant: "destructive",
                  });
                  return;
                }
                if (signatureStrokes.length === 0) {
                  setSignatureError(
                    t("createTicket.signatureRequiredOrCancel"),
                  );
                  return;
                }
                const canvas = canvasRef.current;
                if (!canvas) return;
                const imageDataUrl = canvas.toDataURL("image/png");
                const nextSignature = {
                  signerName: signatureFormName.trim(),
                  signerTitle: signatureFormTitle.trim() || undefined,
                  signerCompany: signatureFormCompany.trim() || undefined,
                  capturedAt: new Date().toISOString(),
                  imageDataUrl,
                };
                setCapturedSignature(nextSignature);
                setSignatureError("");
                setSignatureModalOpen(false);
                if (signatureRequestedFromPreview) {
                  void finalizeOnsiteApproval(nextSignature);
                }
              }}
            >
              {t("createTicket.saveSignature")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={onsitePreviewOpen}
        onOpenChange={(open) =>
          !finalizingOnsiteApproval && setOnsitePreviewOpen(open)
        }
      >
        <DialogContent className="sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle className="text-[#0C1F4F]">
              {t("createTicket.reviewPdfBeforeSignature")}
            </DialogTitle>
            <DialogDescription className="text-[#0C1F4F]/80">
              {t("createTicket.reviewPdfBeforeSignatureDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-hidden rounded-md border bg-muted/20">
            {onsitePreviewUrl ? (
              <iframe
                title={t("createTicket.onSiteTicketPdfPreview")}
                src={`${onsitePreviewUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                className="h-[70vh] w-full bg-white"
              />
            ) : (
              <div className="flex h-[50vh] items-center justify-center text-sm text-muted-foreground">
                {t("createTicket.pdfPreviewUnavailable")}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOnsitePreviewOpen(false)}
              disabled={finalizingOnsiteApproval}
            >
              {t("createTicket.close")}
            </Button>
            <Button
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              onClick={() => {
                setSignatureRequestedFromPreview(true);
                openSignatureModal();
              }}
              disabled={finalizingOnsiteApproval}
            >
              <Pen className="mr-2 h-4 w-4" />{" "}
              {t("createTicket.signThisDocument")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
